import i18next from 'i18next';
import _ from 'lodash';
import { createSelector } from 'reselect';
import {
  sqDurationStore,
  sqTrendConditionStore,
  sqTrendMetricStore,
  sqTrendSeriesStore,
  sqTrendStore,
  sqWorksheetStore,
} from '@/core/core.stores';
import { Store } from '@/core/flux.service';
import { formatDuration, getCapsuleFormula, secondsToMillis } from '@/datetime/dateTime.utilities';
import { STRING_UOM } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { ProcessTypeEnum } from '@/sdk/model/ThresholdMetricOutputV1';
import {
  ASSET_PATH_COLUMN_PREFIX,
  ASSET_PATH_ID_COLUMN_PREFIX,
  COLUMN_PREFIX,
  CONDITION_EXTRA_COLUMNS,
  CONDITION_TABLE_DEFAULT_COLUMNS,
  ITEM_UOM,
  MAX_CONDITION_TABLE_CAPSULES,
  NULL_PLACEHOLDER,
  PREDEFINED_COLUMN_INDEX,
  SEARCH_ANCESTOR_BACKING_TABLE_COLUMN,
  SEARCH_ANCESTOR_COLUMN_NAME,
  SIMPLE_METRIC_FALLBACK_STATISTIC,
  SIMPLE_TABLE_DEFAULT_COLUMNS,
  SIMPLE_TABLE_ID_COLUMN,
  TRANSPOSE_HEADER_COLUMNS,
  TableBuilderColumnType,
  TableBuilderHeaderType,
  TableBuilderMode,
  withDefaultFormatting,
} from '@/tableBuilder/tableBuilder.constants';
import { tableBuilder } from '@/tableBuilder/tableBuilder.utilities';
import { findItemIn, getAllItems, getTrendStores } from '@/trend/trendDataHelper.utilities';
import { isBackendRowsLimitError } from '@/trendData/trend.utilities';
import {
  COLUMNS_AND_STATS,
  ENUM_REGEX,
  ITEM_TYPES,
  TREND_CONDITION_STATS,
  TREND_PANELS,
  TREND_SIGNAL_STATS,
} from '@/trendData/trendData.constants';
import {
  getBuildAdditionalFormula,
  getBuildStatFormulaFunctionCallback,
  getStringPropertyFetchParams,
  getStringStatFetchParams,
} from '@/utilities/formula.utilities';
import { base64guid } from '@/utilities/guid.utilities';
import { isThresholdMetric } from '@/utilities/items.types';
import { isItemRedacted } from '@/utilities/redaction.utilities';
import { deepEqualSelector } from '@/utilities/selector.utilities';
import {
  formatMetricValue,
  isPropertyColumnType,
  isPropertyOrStatColumn,
  isPropertyOrStatOrMetricColumn,
} from '@/utilities/tableBuilderHelper.utilities';
import { infoToast } from '@/utilities/toast.utilities';
import {
  equalsIgnoreCase,
  getCapsuleDuration,
  getShortIdentifier,
  isStringSeries as isStringSeriesUtil,
} from '@/utilities/utilities';
import { WORKSHEET_VIEW } from '@/worksheet/worksheet.constants';
import type { ChartModel } from 'ag-grid-community';
import type { PersistenceLevel } from '@/core/flux.service';
import type { TableColumnFilter } from '@/core/tableUtilities/tables';
import type { ItemFinderInputV1 } from '@/sdk/model/ItemFinderInputV1';
import type { ThresholdOutputV1 } from '@/sdk/model/ThresholdOutputV1';
import type { AgGridAggregationFunction, MetricPropertyColumn } from '@/tableBuilder/tableBuilder.constants';
import type {
  AgGridChartMenuSettings,
  ColumnOrRow,
  ColumnOrRowWithDefinitions,
  ColumnPosition,
  ColumnToThresholdsCondition,
  ColumnToThresholdsSimple,
  ColumnWithIndex,
  ConditionTableCapsule,
  ConditionTableColumnsAndRows,
  ConditionTableData,
  ConditionTableHeader,
  ConditionTableValue,
  ItemColumnsMap,
  OtherColumns,
  SimpleTableCell,
  SimpleTableResultTable,
  SimpleTableRow,
  TableBuilderHeaders,
  TableBuilderRowData,
  TableColumn,
  TreemapBreadcrumbs,
  TreemapParent,
  TreemapViewSettings,
} from '@/tableBuilder/tableBuilder.types';
import type { PropertyColumn, StatisticColumn } from '@/trendData/trendData.constants';
import type { ConditionItem, TableItem } from '@/trendData/trendData.types';
import type {
  BuildAdditionalCapsuleTableFormulaCallback,
  BuildConditionFormulaCallback,
  BuildStatFormulaCallback,
  CapsuleFormulaTableRow,
  FetchParamsForColumn,
  PropertyColumn as FormulaPropertyColumn,
  ParametersMap,
  StatColumn,
} from '@/utilities/formula.constants';
import type { Item, Signal } from '@/utilities/items.types';
import type { FormatOptions } from '@/utilities/numberHelper.utilities';

const GENERIC_ITEM_IDENTIFIER = 'series';
interface MetricsMap {
  [itemGuid: string]: { [aggregationFunction: string]: string };
}

interface TableBuilderMetricDefinition {
  aggregationFunction?: string;
  measuredItem?: Pick<Item, 'id'>;
  thresholds?: ThresholdOutputV1[];
  processType?: ProcessTypeEnum;
  aggregationConditionId?: string;
  boundingCondition?: { id?: string } | null;
  [key: string]: unknown;
}

export type TableBuilderItem = Partial<Item> & {
  id: string;
  itemType: ITEM_TYPES;
  formatOptions?: FormatOptions | string;
  definition?: TableBuilderMetricDefinition | null;
  color?: string;
  columnName?: string;
  rowName?: string;
  tableName?: string;
  signalMetadata?: Signal['signalMetadata'];
  valueUnitOfMeasure?: Signal['valueUnitOfMeasure'];
  sourceValueUnitOfMeasure?: Signal['sourceValueUnitOfMeasure'];
  assets?: Item['assets'];
  tableId?: string;
  columnId?: string;
  rowId?: string;
  [key: string]: unknown;
};

type TableBuilderMetricItem = TableBuilderItem & {
  itemType: ITEM_TYPES.METRIC;
  definition: TableBuilderMetricDefinition;
};

type TableBuilderSignalItem = TableBuilderItem & Signal;

type SimpleTableStringFetchParams = {
  fetchParamsList: Array<{
    formula: string;
    parameters: Record<string, string>;
    root: string | undefined;
    reduceFormula?: string;
  }>;
  columnKeysNamesList: Array<{ columnKey: string; columnNames: string[] }>;
};

type TableBuilderPropertyColumn = FormulaPropertyColumn &
  Omit<PropertyColumn, 'propertyName'> & {
    propertyExpression?: string;
  };

type ConditionPropertyColumnDefinition =
  | (FormulaPropertyColumn & { propertyExpression?: string })
  | (PropertyColumn & { propertyExpression?: string })
  | (ColumnOrRowWithDefinitions & { propertyExpression?: string });

type ConditionTableFetchParams = {
  ids: string[];
  assetId: string | undefined;
  propertyColumns: TableBuilderPropertyColumn[];
  statColumns: StatColumn[];
  customPropertyName?: string;
  buildAdditionalFormula: BuildAdditionalCapsuleTableFormulaCallback;
  itemColumnsMap: ItemColumnsMap;
  buildConditionFormula: BuildConditionFormulaCallback;
  buildStatFormula: BuildStatFormulaCallback;
};

type SimpleTableFetchResult = {
  formula: string;
  parameters: ParametersMap;
  root: string | undefined;
  columnPositions: ColumnPosition[];
};

const normalizeFormatOptions = (formatOptions?: FormatOptions | string): FormatOptions =>
  typeof formatOptions === 'string' ? { format: formatOptions } : (formatOptions ?? {});

interface TableBuilderState {
  mode: TableBuilderMode;
  headers: Record<TableBuilderMode, { type: TableBuilderHeaderType; format: string }>;
  columns: Record<TableBuilderMode, ColumnOrRowWithDefinitions[]>;
  otherColumns: OtherColumns;
  tableData: Record<TableBuilderMode, ConditionTableData | SimpleTableRow[]>;
  autoGroupColumn: Record<TableBuilderMode, Record<string, unknown> | undefined>;
  rowGroupPaths: Record<TableBuilderMode, string[]>;
  isTransposed: Record<TableBuilderMode, boolean>;
  assetId: Record<TableBuilderMode, string | undefined>;
  isHomogenizeUnits: Record<TableBuilderMode, boolean>;
  distinctStringValueMap: Record<TableBuilderMode, Record<string, string[]>>;
  columnIdToAutoHeight: Record<TableBuilderMode, Record<string, boolean>>;
  hasMoreData: boolean;
  fetchFailedMessage: { message: string; apiMessage: string } | undefined;
  isMigrating: boolean;
  clipboardStyle: Record<string, unknown>;
  treemapView: {
    enabled: boolean;
    settings: {
      breadcrumbs: TreemapBreadcrumbs;
      helpKey: string | undefined;
      parent: Partial<TreemapParent>;
      tree?: unknown;
    };
  };
  chartView: {
    enabled: boolean;
    // Avoid ChartModel here to keep schema generation from failing
    settings: Record<string, unknown> | undefined;
    conditionSettings: Record<string, unknown> | undefined;
    conditionEnabled: boolean;
  };
  chartMenuSettings: Record<TableBuilderMode, AgGridChartMenuSettings>;
  areAllRowsExpanded: boolean | undefined;
  useSignalColorsInChart: boolean;
  assetPaths: Array<{
    id: string;
    value?: unknown;
    backingTableColumn: string;
    level?: number;
  }>;
  itemFinderId: string | undefined;
  tableDefinitionId: string | undefined;
  isFormulaAcrossTable: Record<TableBuilderMode, boolean>;
  isFormulaAcrossTableProcessing: boolean;
  itemFinderInput: ItemFinderInputV1 | undefined;
  tableFetchAttempted: boolean;
  assetPathStateHash: string | undefined;
}

export type TableBuilderDehydratedState = Omit<
  TableBuilderState,
  | 'tableData'
  | 'columnIdToAutoHeight'
  | 'clipboardStyle'
  | 'distinctStringValueMap'
  | 'fetchFailedMessage'
  | 'tableFetchAttempted'
>;

export class TableBuilderStore extends Store {
  static readonly storeName = 'sqTableBuilderStore';
  persistenceLevel: PersistenceLevel = 'WORKSHEET';

  /**
   * Initializes the store by setting default values to the stored state
   */
  initialize(): void {
    /**
     * When adding a property that needs to be rehydrated via a saved template, make sure to also update
     * STORES_REHYDRATED_BY_WORKSTEP_TEMPLATE in the worksheet.utilities.ts file
     */
    const initialState: TableBuilderState = {
      mode: TableBuilderMode.Simple,
      headers: {
        [TableBuilderMode.Condition]: {
          type: TableBuilderHeaderType.StartEnd,
          format: 'lll',
        },
        [TableBuilderMode.Simple]: {
          type: TableBuilderHeaderType.StartEnd,
          format: 'lll',
        },
      },
      columns: {
        [TableBuilderMode.Condition]: CONDITION_TABLE_DEFAULT_COLUMNS,
        [TableBuilderMode.Simple]: SIMPLE_TABLE_DEFAULT_COLUMNS,
      },
      otherColumns: {
        [TableBuilderMode.Condition]: {},
        [TableBuilderMode.Simple]: {},
      },
      tableData: {
        [TableBuilderMode.Condition]: { headers: [], capsules: [] },
        [TableBuilderMode.Simple]: [],
      },
      autoGroupColumn: {
        [TableBuilderMode.Condition]: undefined,
        [TableBuilderMode.Simple]: undefined,
      },
      rowGroupPaths: {
        [TableBuilderMode.Condition]: [],
        [TableBuilderMode.Simple]: [],
      },
      isTransposed: {
        [TableBuilderMode.Condition]: true,
        [TableBuilderMode.Simple]: false,
      },
      assetId: {
        [TableBuilderMode.Condition]: undefined,
        [TableBuilderMode.Simple]: undefined,
      },
      isHomogenizeUnits: {
        [TableBuilderMode.Condition]: false,
        [TableBuilderMode.Simple]: false,
      },
      distinctStringValueMap: {
        [TableBuilderMode.Condition]: {},
        [TableBuilderMode.Simple]: {},
      },
      columnIdToAutoHeight: {
        [TableBuilderMode.Condition]: {},
        [TableBuilderMode.Simple]: {},
      },
      hasMoreData: false,
      fetchFailedMessage: undefined,
      isMigrating: false,
      clipboardStyle: {},
      treemapView: {
        enabled: false,
        settings: {
          breadcrumbs: [],
          helpKey: undefined,
          parent: {},
          tree: undefined,
        },
      },
      chartView: {
        enabled: false,
        settings: undefined,
        conditionSettings: undefined,
        conditionEnabled: false,
      },
      chartMenuSettings: {
        [TableBuilderMode.Condition]: {
          isOpened: true,
          panel: 'settings',
        },
        [TableBuilderMode.Simple]: {
          isOpened: true,
          panel: 'settings',
        },
      },
      areAllRowsExpanded: undefined,
      useSignalColorsInChart: true,
      assetPaths: [
        {
          id: base64guid(),
          value: undefined,
          backingTableColumn: SEARCH_ANCESTOR_BACKING_TABLE_COLUMN,
          level: undefined,
        },
      ],
      itemFinderId: undefined,
      tableDefinitionId: undefined,
      isFormulaAcrossTable: {
        [TableBuilderMode.Condition]: false,
        [TableBuilderMode.Simple]: false,
      },
      isFormulaAcrossTableProcessing: false,
      itemFinderInput: undefined,
      tableFetchAttempted: false,
      assetPathStateHash: undefined,
    };
    this.state = this.immutable(initialState);
  }

  get hasMoreData(): boolean {
    return this.state.get('hasMoreData');
  }

  /**
   * Returns the table builder mode (Condition or Simple)
   */
  get mode(): TableBuilderMode {
    return this.state.get('mode');
  }

  /**
   * The header settings for the table
   */
  get headers(): TableBuilderHeaders {
    return this.state.get('headers', this.state.get('mode'));
  }

  /**
   * The columns for a table, with both their settings and custom text
   */
  get columns(): ColumnOrRowWithDefinitions[] {
    return this.getColumnsWithDefinition({ columns: this.getColumns(), autoGroupColumn: this.autoGroupColumn });
  }

  get conditionTableColumns(): ConditionTableColumnsAndRows {
    return this.getConditionTableColumns({
      columns: this.columns,
      workingSetOfItems: this.getTableItemsProcess(),
    });
  }

  get otherColumns(): OtherColumns {
    return this.state.get('otherColumns');
  }

  /**
   * Whether or not we always want to set the given column to calculate it's row height automatically. Necessary for
   * when a column includes newlines
   */
  get columnIdToAutoHeight(): Record<string, boolean> {
    return this.state.get('columnIdToAutoHeight', this.state.get('mode'));
  }

  /**
   * Custom item/capsule properties used in the table.
   */
  get propertyColumns(): PropertyColumn[] {
    return this.getPropertyColumns(this.getColumns());
  }

  /**
   * @returns the value of the headerOverridden flag for the specified column
   */
  getOverriddenHeaderColumn: (columns: ColumnOrRow[]) => ColumnOrRow | undefined = createSelector(
    (columns: ColumnOrRow[]) => columns,
    (columns) => _.find(columns, 'headerOverridden'),
  );

  get overriddenHeaderColumn(): ColumnOrRow | undefined {
    return this.getOverriddenHeaderColumn(this.getColumns());
  }

  /** Changes when filter or sort or column order changes */
  get columnsWithIndex(): ColumnWithIndex[] {
    return this.getColumnsWithIndex({
      columns: this.columns,
      conditionTableRows: this.conditionTableColumns.rows,
      mode: this.mode,
    });
  }

  get rawConditionTableData(): ConditionTableData {
    return this.state.get('tableData', TableBuilderMode.Condition);
  }

  /**
   * Data generated for the condition table display. This is accessed and set separately from simpleTableData
   * because the asynchronous nature of fetching table data means that there's a chance data could be written
   * in the wrong location if we get/set dependent only on the TableBuilderMode. See CRAB-22970.
   */
  get conditionTableData(): ConditionTableData {
    return this.getConditionTableData({
      columnsWithIndex: this.columnsWithIndex,
      rawTableData: this.rawConditionTableData,
      isTransposed: this.isTransposed,
    });
  }

  get rawSimpleTableData(): SimpleTableRow[] {
    return this.state.get('tableData', TableBuilderMode.Simple);
  }

  /**
   * Data generated for the simple table display. This is accessed and set separately from conditionTableData
   * because the asynchronous nature of fetching table data means that there's a chance data could be written
   * in the wrong location if we get/set dependent only on the TableBuilderMode. See CRAB-22970.
   */
  get simpleTableData(): SimpleTableRow[] {
    return this.getSimpleTableData({
      columnsWithIndex: this.columnsWithIndex,
      rawTableData: this.rawSimpleTableData,
    });
  }

  get isTransposed(): boolean {
    return this.state.get('isTransposed', this.state.get('mode'));
  }

  get assetId(): string | undefined {
    return this.getAssetId();
  }

  get isHomogenizeUnits(): boolean {
    return this.getIsHomogenizeUnits();
  }

  get isMigrating(): boolean {
    return this.state.get('isMigrating');
  }

  get isTableStriped(): boolean | undefined {
    return this.state.get('isTableStriped', this.state.get('mode'));
  }

  get useSignalColorsInChart(): boolean {
    return this.state.get('useSignalColorsInChart');
  }

  get assetPaths(): { id: string; value: number | undefined; backingTableColumn: string; level?: number }[] {
    return this.state.get('assetPaths');
  }

  get fetchFailedMessage(): { message: string; apiMessage: string } | undefined {
    return this.state.get('fetchFailedMessage');
  }

  get distinctStringValueMap(): Record<TableBuilderMode, Record<string, string[]>> {
    return this.state.get('distinctStringValueMap');
  }

  get chartMenuSettings(): AgGridChartMenuSettings {
    return this.state.get(['chartMenuSettings', this.state.get('mode')]);
  }

  /**
   * See if the chartView is enabled or not
   */
  get showChartView(): boolean {
    return this.state.get('chartView', 'enabled');
  }

  /**
   * Whether or not we've attempted to fetch the table data at least once. Useful for knowing how far in the loading
   * process were are for retrieving table based content
   */
  get tableFetchAttempted(): boolean {
    return this.state.get('tableFetchAttempted');
  }

  /**
   * See if the chartView is enabled or not
   */
  get showConditionChartView(): boolean {
    return this.state.get('chartView', 'conditionEnabled');
  }

  /**
   * See if the treemap is enabled or not
   */
  get showTreemapView(): boolean {
    return this.state.get('treemapView', 'enabled');
  }

  /**
   * Get the value for expand all or collapse all. undefiend means noop.
   */
  get areAllRowsExpanded(): boolean | undefined {
    return this.state.get('areAllRowsExpanded');
  }

  /**
   * Get the chart view settings
   */
  get chartViewSettings(): ChartModel | undefined {
    return this.state.get('chartView', 'settings');
  }

  /**
   * Get the condition table chart view settings
   */
  get chartViewConditionSettings(): ChartModel | undefined {
    return this.state.get('chartView', 'conditionSettings');
  }

  /**
   * Get the tree map view settings
   */
  get treemapViewSettings(): {
    breadcrumbs: TreemapBreadcrumbs;
    helpKey: string | undefined;
    parent: Partial<TreemapParent>;
    tree: unknown;
  } {
    return this.state.get('treemapView', 'settings');
  }

  /**
   * Returns the Seeq-stored state for the grouping column in ag-grid
   */
  get autoGroupColumn(): Record<string, unknown> | undefined {
    return this.state.get('autoGroupColumn', this.state.get('mode'));
  }

  get rowGroupOrder(): string[] {
    return this.state.get('rowGroupOrder', this.state.get('mode'));
  }

  get rowGroupPaths(): string[] {
    return this.state.get('rowGroupPaths', this.state.get('mode'));
  }

  get itemFinderId(): string {
    return this.state.get('itemFinderId');
  }

  get tableDefinitionId(): string {
    return this.state.get('tableDefinitionId');
  }

  get isFormulaAcrossTable(): boolean {
    return this.state.get('isFormulaAcrossTable', this.state.get('mode'));
  }

  get isFormulaAcrossTableProcessing(): boolean {
    return this.state.get('isFormulaAcrossTableProcessing');
  }

  get itemFinderInput(): ItemFinderInputV1 {
    return this.state.get('itemFinderInput');
  }

  get assetPathStateHash(): string | undefined {
    return this.state.get('assetPathStateHash');
  }

  /**
   * @returns true if simple table mode is active and false is condition table mode is active
   */
  isSimpleMode(): boolean {
    return this.getIsSimpleMode();
  }

  /**
   * @returns the items to be displayed in the table filtered based on simple/condition mode.
   */
  getTableItems(): TableBuilderItem[] {
    return this.getTableItemsProcess();
  }

  statRowsIsSelected(data: TableBuilderRowData[]) {
    return data.some(
      (row) => row.tableBuilderHeaderColumnId?.startsWith('statistic') || row.key === COLUMNS_AND_STATS.metricValue.key,
    );
  }

  private normalizePropertyColumn(column: ConditionPropertyColumnDefinition): TableBuilderPropertyColumn {
    const propertyColumnCandidate = column as PropertyColumn & { propertyExpression?: string };
    const propertyNameSource = propertyColumnCandidate.propertyName;
    const propertyName =
      typeof propertyNameSource === 'function'
        ? propertyNameSource(ITEM_TYPES.CONDITION)
        : (propertyNameSource ?? column.key);

    return {
      ...column,
      propertyName,
    } as TableBuilderPropertyColumn;
  }

  /**
   * Builds the necessary information to fetch the simple table.
   *
   * @param itemsOfTheSameType - Items of the same type that are used to construct and organize needed information that
   * will be used to
   * compute the table.
   * @returns An object containing the formula, parameters and the positions of each column which can be used in
   * conjunction with #setSimpleTableData()
   */
  getSimpleTableFetchParams(itemsOfTheSameType: TableBuilderItem[]): SimpleTableFetchResult {
    const item = _.first(itemsOfTheSameType);
    const items = this.getTableItemsProcess();
    if (items.length === 0 || !item) {
      return {
        formula: '',
        parameters: {},
        root: undefined,
        columnPositions: [],
      } as SimpleTableFetchResult;
    }

    const columnPositions: ColumnPosition[] = [];
    const columnFormulas: string[] = [];
    const isSignal = item.itemType === ITEM_TYPES.SERIES;
    const isStringSeries = isStringSeriesUtil(item as Signal);
    const isCondition = item.itemType === ITEM_TYPES.CONDITION;
    const statColumns: ColumnOrRowWithDefinitions[] = [];
    const itemIdentifier = GENERIC_ITEM_IDENTIFIER;
    const isMetric = item.itemType === ITEM_TYPES.METRIC;

    const isRunAcrossAssets = !!this.getAssetId();
    const isHomogenizeUnits = this.getIsHomogenizeUnits();
    let resultIndex = 2; // First two columns are start and end time
    let identifierIndex = 0;
    const parameters: ParametersMap = {};

    const columns = _.chain(this.columns)
      .reject({
        type: TableBuilderColumnType.Text,
      })
      .thru((columns) => {
        if (!_.some(columns, (column) => column.key === COLUMNS_AND_STATS.valueUnitOfMeasure.key)) {
          columns.push(COLUMNS_AND_STATS.valueUnitOfMeasure);
        }
        if (isMetric) {
          columns.push({
            key: SeeqNames.Properties.MeasuredItemId,
            propertyName: SeeqNames.Properties.MeasuredItemId,
            style: 'string',
          });
          columns.push({
            key: SeeqNames.Properties.AggregationFunction,
            propertyName: SeeqNames.Properties.AggregationFunction,
            style: 'string',
          });
        }
        return columns;
      })
      .value() as ColumnOrRowWithDefinitions[];

    //Item ID is arbitrary and will be replaced on the backend with the specific ID of the item. Sending empty guid
    // won't work since parameters are validated on the backend.
    parameters[itemIdentifier] = item.id;
    // All items need a column with their ID so that there is a unique identifier when run across assets
    const idIdentifier = `series_id`;
    parameters[idIdentifier] = `$series.property('${SeeqNames.Properties.Id}')`;
    columnPositions.push({
      key: SIMPLE_TABLE_ID_COLUMN,
      index: resultIndex++,
    });
    columnFormulas.push(`.addColumn('${itemIdentifier}.${COLUMN_PREFIX}ID', $${idIdentifier})`);
    // The column name for a metric value is the itemId, but the name of a metric column that
    // is associated with a stat column is: <measuredItemShortId>.<metricId>
    const buildConvertUnitsFormulaFragment = (itemId: string, columnName?: string): string => {
      let convertUnits = '';
      if (isRunAcrossAssets && !isStringSeries) {
        let uom: string;
        if (isHomogenizeUnits) {
          const fixedMetricUOMIdentifier = getShortIdentifier(identifierIndex++);
          parameters[fixedMetricUOMIdentifier] = `$series.property('${SeeqNames.Properties.SwapSourceUom}')`;
          uom = `$${fixedMetricUOMIdentifier}`;
        } else {
          // remove units when we run across assets and homogenize units is not possible
          uom = "''";
        }
        convertUnits = `.convertUnits('${columnName ?? itemId}', ${uom})`;
      }
      return convertUnits;
    };

    if (isMetric && !_.some(columns, (column) => column.key === COLUMNS_AND_STATS.metricValue.key)) {
      // Add metricValue to all metrics, even if the column isn't being displayed, so that the priority color can also be applied if the corresponding statistic and measured item are configured to show in the table.
      const convertUnits = buildConvertUnitsFormulaFragment(item.id);
      columnFormulas.push(`.addSimpleMetricColumn('${item.id}', $${itemIdentifier})${convertUnits}`);
      columnPositions.push({
        key: COLUMNS_AND_STATS.metricValue.key,
        index: resultIndex,
        metricId: item.id,
      });
      resultIndex += 2;
    }

    _.forEach(columns, (column) => {
      if (column.style === 'metric') {
        if (isThresholdMetric(item as Item)) {
          const convertUnits = buildConvertUnitsFormulaFragment(item.id);
          columnFormulas.push(`.addSimpleMetricColumn('${item.id}', $${itemIdentifier})${convertUnits}`);
          columnPositions.push({
            key: column.key,
            index: resultIndex,
            metricId: item.id,
          });
          // Metrics add two columns, one for value, and one for color
          resultIndex += 2;
        }
      } else if (column.stat) {
        const isStatColumn =
          (isSignal &&
            _.some(TREND_SIGNAL_STATS, ['stat', column.stat]) &&
            (!isStringSeries || column.isStringCompatible)) ||
          (isCondition && _.some(TREND_CONDITION_STATS, ['stat', column.stat]));

        if (!isMetric && isStatColumn) {
          statColumns.push(column);
        }
      } else {
        let propertyFormula = this.getSimpleTablePropertyColumnFormula(column, item, itemIdentifier);
        if (
          isHomogenizeUnits &&
          isRunAcrossAssets &&
          !isStringSeries &&
          column.key === COLUMNS_AND_STATS.valueUnitOfMeasure.key
        ) {
          // If homogenizing units then we need to know the UOM of the unswapped (swapOut) item
          propertyFormula = propertyFormula.replace(SeeqNames.Properties.ValueUom, SeeqNames.Properties.SwapSourceUom);
        }
        const propertyIdentifier = getShortIdentifier(identifierIndex++);
        parameters[propertyIdentifier] = propertyFormula;
        columnFormulas.push(`.addColumn('${itemIdentifier}.${column.key}', $${propertyIdentifier})`);

        columnPositions.push({
          key: column.key,
          index: resultIndex++,
        });
      }
    });

    // Add all the statistic columns as a group for best backend performance
    if (statColumns.length > 0) {
      _.forEach(statColumns, (column) => {
        columnPositions.push({
          key: column.key,
          index: resultIndex++,
        });
      });
      const stats = _.chain(statColumns).map('stat').join(', ').value();
      let itemReference = `$${itemIdentifier}`;
      // convertUnits does not accept conditions as input. Not an issue because our current condition stats are
      // unitless.
      if (isSignal && isRunAcrossAssets && !isStringSeries) {
        if (isHomogenizeUnits) {
          const fixedItemUOMIdentifier = getShortIdentifier(identifierIndex++);
          parameters[fixedItemUOMIdentifier] = `$series.property('${SeeqNames.Properties.SwapSourceUom}')`;
          itemReference = `$${itemIdentifier}.convertUnits($${fixedItemUOMIdentifier})`;
        } else {
          itemReference = `$${itemIdentifier}.convertUnits('')`;
        }
      }
      columnFormulas.push(`.addStatColumn('${itemIdentifier}', ${itemReference}, ${stats})`);
    }

    let originalItemIdFormula = '';
    if (isRunAcrossAssets) {
      const fixedItemUOMIdentifier = getShortIdentifier(identifierIndex++);
      originalItemIdFormula = `.addColumn('${SeeqNames.Properties.SwapSourceId}', $${fixedItemUOMIdentifier})`;
      parameters[fixedItemUOMIdentifier] = `$series.property('${SeeqNames.Properties.SwapSourceId}')`;
    }

    const viewCapsule = getCapsuleFormula(sqDurationStore.displayRange);
    const columnsFormula = columnFormulas.join('');
    const formula = `group(${viewCapsule}).toTable('simple')${columnsFormula}${originalItemIdFormula}`;
    return {
      formula,
      parameters,
      root: this.getAssetId(),
      columnPositions,
    };
  }

  /**
   * Builds the variables necessary for fetching the condition table.
   *
   * @returns An object containing the item necessary for fetch and used in conjunction with #setConditionTableData
   */
  getConditionTableFetchParams(): ConditionTableFetchParams {
    const items = this.getTableItemsProcess();
    const assetId = this.getAssetId();
    const isRunAcrossAssets = !!this.getAssetId();
    const isHomogenizeUnits = this.getIsHomogenizeUnits();
    const allColumns = this.columns;
    const metrics = items.filter((item) => item.itemType === ITEM_TYPES.METRIC) as TableBuilderMetricItem[];
    const itemColumnsMap = tableBuilder.metricsToItemColumnsMap(metrics);

    const itemColumns: MetricPropertyColumn[] = _.chain(itemColumnsMap).values().flatMap(_.values).value();

    const propertyColumnCandidates: ConditionPropertyColumnDefinition[] = [
      ...(itemColumns as ConditionPropertyColumnDefinition[]),
      (_.find(allColumns, { key: COLUMNS_AND_STATS.startTime.key }) ||
        COLUMNS_AND_STATS.startTime) as ConditionPropertyColumnDefinition,
      (_.find(allColumns, { key: COLUMNS_AND_STATS.endTime.key }) ||
        COLUMNS_AND_STATS.endTime) as ConditionPropertyColumnDefinition,
      ...(this.propertyColumns as ConditionPropertyColumnDefinition[]),
      ...(_.filter(allColumns, 'propertyExpression') as ConditionPropertyColumnDefinition[]),
    ].map((column) => _.omit(column, 'filter') as ConditionPropertyColumnDefinition);
    const propertyColumns = propertyColumnCandidates.map((column) => this.normalizePropertyColumn(column));

    const statColumns = _.sortBy(
      tableBuilder.getStatColumns(this.columns, sqTrendSeriesStore.findItem),
      'signalId',
    ).map((column) => _.omit(column, 'filter') as StatColumn);

    const conditionPropertyColumns = (
      _.filter(
        allColumns,
        (column) => column.type === TableBuilderColumnType.Property || column.propertyExpression,
      ) as ConditionPropertyColumnDefinition[]
    ).map((column) => this.normalizePropertyColumn(column));
    const buildConditionFormula: BuildConditionFormulaCallback = this.getBuildConditionFormulaFunction(
      items,
      itemColumnsMap,
      conditionPropertyColumns,
      isRunAcrossAssets,
      statColumns,
    );

    const metricIdsForUnitConversion = metrics
      .filter((metric) => !isStringSeriesUtil(metric))
      .map((metric) => String(metric.id));

    const buildAdditionalFormula: BuildAdditionalCapsuleTableFormulaCallback = getBuildAdditionalFormula(
      propertyColumns,
      statColumns,
      false,
      [],
      isRunAcrossAssets,
      metricIdsForUnitConversion,
      isHomogenizeUnits,
    );

    const buildStatFormula: BuildStatFormulaCallback = this.getBuildStatFormulaFunction(statColumns);

    const headers = this.state.get('headers', TableBuilderMode.Condition);
    let customPropertyName: string | undefined;
    if (headers.type === TableBuilderHeaderType.CapsuleProperty) {
      customPropertyName = headers.property;
      // Account for properties that are already in the table but may have different names/keys
      // (e.g. the property Start uses the key startTime)
      const customPropertyColumn = _.find(propertyColumns, (column) =>
        equalsIgnoreCase(column.propertyName, customPropertyName),
      );
      if (!customPropertyColumn) {
        propertyColumns.push(
          this.normalizePropertyColumn({
            key: customPropertyName,
            invalidsFirst: true,
            propertyName: customPropertyName,
          } as PropertyColumn),
        );
      } else {
        customPropertyName = customPropertyColumn.key;
      }
    }

    const ids = _.map(items, (item) => String(item.id));

    return {
      ids,
      assetId,
      propertyColumns,
      statColumns,
      customPropertyName,
      buildAdditionalFormula,
      itemColumnsMap,
      buildConditionFormula,
      buildStatFormula,
    };
  }

  /**
   * Gets the necessary information to fetch distinct string values that appear in different Simple Table columns.
   * Each set of fetch params returned corresponds to one column in the displayed Simple Table.  The fetch params
   * correspond to a table that has only the necessary information to get the column's string values.
   *
   * @returns obj.fetchParamsList: array of fetch param objects, each with a table formula
   *          obj.columnKeysNamesList: array of {columnKey, columnNames} objects where columnKey is the key of
   *            the displayed frontend table, and columnNames contains the names of the corresponding columns in
   *            the computed table returned from the backend, since those columns are combined to form the
   *            displayed Simple Table.
   */
  getSimpleTableStringColumnsFetchParams(): SimpleTableStringFetchParams {
    const items = this.getTableItemsProcess();
    const signals = _.filter(items, (item) => item.itemType === ITEM_TYPES.SERIES) as TableBuilderSignalItem[];
    const metrics = _.filter(items, (item) => item.itemType === ITEM_TYPES.METRIC) as TableBuilderMetricItem[];
    const root = this.getAssetId();
    const isRunAcrossAssets = !!root;
    const columns = _.reject(this.columns, {
      type: TableBuilderColumnType.Text,
    }) as ColumnOrRowWithDefinitions[];
    const columnKeysNamesList: SimpleTableStringFetchParams['columnKeysNamesList'] = [];
    const fetchParamsList: SimpleTableStringFetchParams['fetchParamsList'] = [];
    const hasOnlyStringSeries = !_.isEmpty(signals) && _.every(signals, (signal) => isStringSeriesUtil(signal));
    const hasOnlyStringMetrics = !_.isEmpty(metrics) && _.every(metrics, (metric) => isStringSeriesUtil(metric));
    const viewCapsule = getCapsuleFormula(sqDurationStore.displayRange);
    const baseFormula = `group(${viewCapsule}).toTable('simple')`;
    let identifierIndex = 0;

    _.forEach(columns, (column) => {
      if (hasOnlyStringSeries && column.stat && column.key === 'statistics.endValue') {
        let formula = baseFormula;
        const columnNames: string[] = [];
        const parameters: Record<string, string> = {};
        _.forEach(signals, (signal) => {
          const signalIdentifier = getShortIdentifier(identifierIndex++);
          parameters[signalIdentifier] = String(signal.id);
          columnNames.push(`${signalIdentifier} ${column.columnSuffix}`);
          formula = `${formula}.addStatColumn('${signalIdentifier}', $${signalIdentifier}, ${column.stat})`;
        });
        const additionalFormula = `.distinctColumnValues(${_.map(columnNames, (name) => `'${name}'`).join(', ')})`;
        formula = `${formula}${isRunAcrossAssets ? '' : additionalFormula}`;
        const reduceFormula = isRunAcrossAssets ? `$result${additionalFormula}` : undefined;
        fetchParamsList.push({
          formula,
          parameters,
          root,
          reduceFormula,
        });
        columnKeysNamesList.push({
          columnKey: column.key,
          columnNames,
        });
      } else if (hasOnlyStringMetrics && column.key === COLUMNS_AND_STATS.metricValue.key) {
        // metric value column for string metrics
        let formula = baseFormula;
        const columnNames: string[] = [];
        const parameters: Record<string, string> = {};
        _.forEach(metrics, (metric) => {
          const metricIdentifier = getShortIdentifier(identifierIndex++);
          parameters[metricIdentifier] = String(metric.id);
          columnNames.push(String(metric.id));
          formula = `${formula}.addSimpleMetricColumn('${metric.id}', $${metricIdentifier})`;
        });
        const additionalFormula = `.distinctColumnValues(${_.map(columnNames, (name) => `'${name}'`).join(', ')})`;
        formula = `${formula}${isRunAcrossAssets ? '' : additionalFormula}`;
        const reduceFormula = isRunAcrossAssets ? `$result${additionalFormula}` : undefined;
        fetchParamsList.push({
          formula,
          parameters,
          root,
          reduceFormula,
        });
        columnKeysNamesList.push({
          columnKey: column.key,
          columnNames,
        });
      } else if (_.includes(['string', 'assets', 'fullpath', 'assetPath'], column.style) || column.isCustomProperty) {
        // property columns
        let formula = baseFormula;
        const columnNames: string[] = [];
        const parameters: Record<string, string> = {};
        _.forEach(items, (item) => {
          const itemIdentifier = getShortIdentifier(identifierIndex++);
          parameters[itemIdentifier] = String(item.id);
          const propertyFormula = this.getSimpleTablePropertyColumnFormula(column, item, itemIdentifier);
          const propertyIdentifier = getShortIdentifier(identifierIndex++);
          parameters[propertyIdentifier] = propertyFormula;
          columnNames.push(`${itemIdentifier}.${column.key}`);
          formula = `${formula}.addColumn('${itemIdentifier}.${column.key}', $${propertyIdentifier})`;
        });
        const additionalFormula = `.distinctColumnValues(${_.map(columnNames, (name) => `'${name}'`).join(', ')})`;
        formula = `${formula}${isRunAcrossAssets ? '' : additionalFormula}`;
        const reduceFormula = isRunAcrossAssets ? `$result${additionalFormula}` : undefined;
        fetchParamsList.push({
          formula,
          parameters,
          root,
          reduceFormula,
        });
        columnKeysNamesList.push({
          columnKey: column.key,
          columnNames,
        });
      }
    });

    return { fetchParamsList, columnKeysNamesList };
  }

  /**
   * Gets the necessary information to fetch distinct string values for a pick-list for column.
   *
   * @param columnKey - string column that is being filtered, to fetch string values for.
   */
  getConditionTableStringColumnFetchParams(columnKey: string): Promise<FetchParamsForColumn> {
    const items = this.getTableItemsProcess();
    const assetId = this.getAssetId();
    const isRunAcrossAssets = !!this.getAssetId();
    const allColumns = this.columns;
    const statColumns = tableBuilder.getStatColumns(this.columns, sqTrendSeriesStore.findItem);

    // String-valued metrics
    const metrics = _.filter(items, { itemType: ITEM_TYPES.METRIC }) as TableBuilderMetricItem[];
    const metric = _.find(metrics, (candidate) => candidate.id === columnKey && isStringSeriesUtil(candidate));
    const itemColumnsMap = tableBuilder.metricsToItemColumnsMap(metrics);
    if (metric) {
      const columns = itemColumnsMap[metric.id];
      const columnCandidates: ConditionPropertyColumnDefinition[] = [
        ...(_.values(columns) as ConditionPropertyColumnDefinition[]),
        (_.find(allColumns, { key: COLUMNS_AND_STATS.startTime.key }) ||
          COLUMNS_AND_STATS.startTime) as ConditionPropertyColumnDefinition,
        (_.find(allColumns, { key: COLUMNS_AND_STATS.endTime.key }) ||
          COLUMNS_AND_STATS.endTime) as ConditionPropertyColumnDefinition,
      ].map((column) => _.omit(column, 'filter') as ConditionPropertyColumnDefinition);
      const propertyColumns = columnCandidates.map((column) => this.normalizePropertyColumn(column));
      const buildAdditionalFormula = () => `.distinctColumnValues('${columns.value.key}')`;
      return Promise.resolve({
        columnKeyAndName: { columnName: columns.value.key, columnKey: metric.id },
        fetchParams: {
          ids: [metric.id],
          assetId,
          propertyColumns,
          statColumns: [],
          itemColumnsMap,
          buildAdditionalFormula: isRunAcrossAssets ? undefined : buildAdditionalFormula,
          reduceFormula: isRunAcrossAssets ? `$result${buildAdditionalFormula()}` : undefined,
          buildConditionFormula: this.getBuildConditionFormulaFunction(
            [metric],
            itemColumnsMap,
            [],
            isRunAcrossAssets,
            statColumns,
          ),
        },
      });
    }

    const statParams = getStringStatFetchParams(
      columnKey,
      statColumns,
      allColumns,
      _.map(items, 'id'),
      (statColumn) => this.getBuildStatFormulaFunction([statColumn]),
      assetId,
      this.getBuildConditionFormulaFunction(items, itemColumnsMap, [], isRunAcrossAssets, statColumns),
    );
    if (statParams) {
      return Promise.resolve(statParams);
    }

    const propertyColumn = _.find(
      this.propertyColumns,
      ({ propertyName }) => propertyName === columnKey,
    ) as PropertyColumn;
    const conditionPropertyColumn = _.find(
      allColumns,
      (column) =>
        columnKey === column.key && (column.type === TableBuilderColumnType.Property || column.propertyExpression),
    );
    if (conditionPropertyColumn) {
      const distinctColumnValuesFormula = `.distinctColumnValues('${conditionPropertyColumn.key}')`;
      const normalizedConditionColumn = this.normalizePropertyColumn(
        conditionPropertyColumn as ConditionPropertyColumnDefinition,
      );
      return Promise.resolve({
        columnKeyAndName: {
          columnKey: conditionPropertyColumn.key,
          columnName: conditionPropertyColumn.key,
        },
        fetchParams: {
          ids: _.map(items, 'id'),
          assetId,
          // Prefer propertyColumn as it is the correct type except when it is a propertyExpression column
          propertyColumns: [
            this.normalizePropertyColumn(
              (propertyColumn ?? conditionPropertyColumn) as ConditionPropertyColumnDefinition,
            ),
          ],
          statColumns: [],
          itemColumnsMap,
          buildAdditionalFormula: () => distinctColumnValuesFormula,
          reduceFormula: isRunAcrossAssets ? `$result${distinctColumnValuesFormula}` : undefined,
          buildConditionFormula: this.getBuildConditionFormulaFunction(
            items,
            itemColumnsMap,
            [normalizedConditionColumn],
            isRunAcrossAssets,
            statColumns,
          ),
        },
      });
    }

    if (propertyColumn) {
      const conditionItems = _.filter(items, { itemType: ITEM_TYPES.CONDITION }) as TableBuilderItem[];
      return getStringPropertyFetchParams(
        conditionItems as unknown as ConditionItem[],
        this.normalizePropertyColumn(propertyColumn as ConditionPropertyColumnDefinition) as FormulaPropertyColumn,
        assetId,
      );
    }

    // There are edge cases with metrics that can result in a property column that doesn't support
    // fetching the possible string values. Specifically, a metric built upon a condition that has a
    // Value property and the user adds the Value (original) column.
    return Promise.resolve({
      fetchParams: {},
      columnKeyAndName: { columnKey, columnName: columnKey },
    });
  }

  /**
   * Create a map between table columns and thresholds that correspond to metrics that are relevant to the column.
   * For stat columns, the map key is the aggregation function, and the value is a list of thresholds belonging
   * to any metrics that use that aggregation function.
   * For the metricValue column, the map key is 'metricValue', and the value is a list of all of the thresholds
   * for any relevant (i.e. simple) metrics.
   * This is used in table filtering to give the user the option of picking an existing threshold to use as a
   * filter.
   *
   * @returns the map between table columns and arrays of thresholds
   */
  getColumnToThresholdsForSimple(): ColumnToThresholdsSimple {
    const columns = this.columns;
    const columnStats = _.chain(columns)
      .filter((column: { stat?: string }) => !!column.stat)
      .map('stat')
      .value();
    const hasMetricValueColumn = _.some(columns, { key: 'metricValue' });
    const itemIds = _.map(this.getTableItems(), 'id');
    return (
      _.chain(
        getAllItems({
          workingSelection: false,
          itemTypes: [ITEM_TYPES.METRIC],
        }),
      )
        .reject((metric) => isItemRedacted(metric))
        .filter((metric) => metric.definition?.processType === ProcessTypeEnum.Simple)
        // filter out the conflictual metrics (multiple metrics with the same aggregation function for the same
        // item)
        .thru((metrics) =>
          _.reject(metrics, (metric) =>
            _.some(
              metrics,
              (otherMetric) =>
                metric.id !== otherMetric.id &&
                metric.definition.measuredItem.id === otherMetric.definition.measuredItem.id &&
                metric.definition.aggregationFunction === otherMetric.definition.aggregationFunction,
            ),
          ),
        )
        .transform((columnToThresholds, metric) => {
          const aggregationFunction = metric.definition.aggregationFunction;
          if (_.includes(columnStats, aggregationFunction) && _.includes(itemIds, metric.definition.measuredItem.id)) {
            columnToThresholds[aggregationFunction] = columnToThresholds[aggregationFunction] ?? [];
            const valueThresholds = _.reject(metric.definition.thresholds, (thresh) => _.isUndefined(thresh.value));
            columnToThresholds[aggregationFunction].push(...valueThresholds);
          }
          if (hasMetricValueColumn && _.includes(itemIds, metric.id)) {
            columnToThresholds.metricValue = columnToThresholds.metricValue ?? [];
            const valueThresholds = _.reject(metric.definition.thresholds, (thresh) => _.isUndefined(thresh.value));
            columnToThresholds.metricValue.push(...valueThresholds);
          }
        }, {} as ColumnToThresholdsSimple)
        .value()
    );
  }

  get columnToThresholdsForCondition(): ColumnToThresholdsCondition {
    return this.getColumnToThresholdsForCondition(this.getTableItemsProcess());
  }

  /**
   * Create a map between table columns and thresholds that correspond to metrics in the table. Used for
   * filtering the condition table.
   *
   * @returns the map between metrics and arrays of thresholds
   */
  getColumnToThresholdsForCondition: (items: any[]) => ColumnToThresholdsCondition = createSelector(
    (items) => _.filter(items, { itemType: ITEM_TYPES.METRIC }),
    (metrics) => {
      return _.transform(
        metrics,
        (result, metric) => {
          result[metric.id] = _.isEmpty(metric.definition?.thresholds) ? undefined : metric.definition.thresholds;
        },
        {} as ColumnToThresholdsCondition,
      );
    },
  );

  /**
   * Checks if the table contains a particular column
   * @param column - The column to check. One of COLUMNS_AND_STATS
   * @param [signalId] - The series if it is a statistic column for condition table
   * @returns true if the table has the column, false otherwise
   */
  isColumnEnabled(column: PropertyColumn | StatisticColumn, signalId: string | null = null) {
    return this.isColumnKeyEnabled(this.getColumnKeyFromSignalId(column, signalId));
  }

  /**
   * Gets the unique key for a column
   * @param column - The column to use. One of COLUMNS_AND_STATS
   * @param [signalId] - The series if it is a statistic column for condition table
   * @returns The unique key
   */
  getColumnKey(column: PropertyColumn | StatisticColumn | TableColumn | ColumnOrRow, signalId: string | null = null) {
    return this.getColumnKeyFromSignalId(column, signalId);
  }

  /**
   * @returns true if simple table mode is active and false is condition table mode is active
   */
  getIsSimpleMode(): boolean {
    return this.state.get('mode') === TableBuilderMode.Simple;
  }

  /**
   * @returns the items to be displayed in the table filtered based on simple/condition mode.
   */
  getTableItemsProcess(): TableBuilderItem[] {
    const simpleMode = this.getIsSimpleMode();
    const itemTypes = simpleMode
      ? [ITEM_TYPES.SERIES, ITEM_TYPES.SCALAR, ITEM_TYPES.CONDITION, ITEM_TYPES.METRIC]
      : [ITEM_TYPES.METRIC, ITEM_TYPES.CONDITION];
    const { sortBy, sortAsc } = sqTrendStore.getPanelSort(TREND_PANELS.SERIES);

    return _.chain(
      (
        getAllItems({
          workingSelection: true,
          itemTypes,
        }) ?? []
      ).concat(
        this.isFormulaAcrossTable && !this.isSimpleMode()
          ? this.getColumns()
              .filter((column) => column.metricId)
              .map((column) => (column as any).item)
          : [],
      ),
    )
      .reject((item) => isItemRedacted(item))
      .filter((item) => {
        if (item.definition?.processType) {
          return simpleMode
            ? item.definition.processType === ProcessTypeEnum.Simple
            : item.definition.processType !== ProcessTypeEnum.Simple;
        } else {
          return true;
        }
      })
      .orderBy([sortBy], [sortAsc ? 'asc' : 'desc'])
      .value() as TableBuilderItem[];
  }

  /**
   * Checks if the table contains a particular column
   * @param key - Column key
   * @returns true if the table has the column, false otherwise
   */
  isColumnKeyEnabled(key: string): boolean {
    return this.getColumnIndex(key, true) > -1;
  }

  /**
   * Gets the unique key for a column
   * @param column - The column being toggled. One of COLUMNS_AND_STATS
   * @param [signalId] - The series if it is a statistic column for condition table
   */
  getColumnKeyFromSignalId(
    column: PropertyColumn | StatisticColumn | TableColumn | ColumnOrRow,
    signalId: string | null = null,
  ): string {
    const key = column.key ?? '';
    return signalId ? `${(column as ColumnOrRow).statisticKey || key}_${signalId}` : key;
  }

  /**
   * Returns the columns for a table as persisted in the store (key, custom text, color). Other attributes like
   * accessor, transformResponse (present in TREND_COLUMNS) are excluded.
   */
  getColumns(): ColumnOrRow[] {
    return this.state.get('columns', this.state.get('mode'));
  }

  /**
   * Returns the columns for a table, with their definition if it is a statistic or property.
   */
  getColumnsWithDefinition: (state: {
    columns: ColumnOrRow[];
    autoGroupColumn: Record<string, unknown> | undefined;
  }) => ColumnOrRowWithDefinitions[] = createSelector(
    ({ columns }) => columns,
    ({ autoGroupColumn }) => autoGroupColumn,
    (columns, autoGroupColumn) =>
      columns
        .map((column) => ({
          ...((COLUMNS_AND_STATS as any)[column.statisticKey || column.key] || {}),
          ...column,
        }))
        .map((column: ColumnOrRowWithDefinitions) =>
          autoGroupColumn
            ? column
            : (_.omit(column, ['grouping', 'rowGroupOrder', 'aggregationFunction']) as ColumnOrRowWithDefinitions),
        ),
  );

  getConditionTableColumns: (state: {
    columns: ColumnOrRowWithDefinitions[];
    workingSetOfItems: { id: string }[];
  }) => ConditionTableColumnsAndRows = createSelector(
    ({ columns }) => columns,
    deepEqualSelector(
      ({ workingSetOfItems }) => workingSetOfItems,
      (items) => items.map((item) => item.id),
    ),
    (columnsWithDefinition, itemIds) => {
      const [rows, columns] = _.partition(columnsWithDefinition, (column) => isPropertyOrStatOrMetricColumn(column));
      return {
        rows: rows
          .map((row) => ({ ...row, isPropertyOrStatColumn: isPropertyOrStatColumn(row) }))
          .filter((column) => !column.metricId || itemIds.some((id) => id === column.metricId)),
        columns,
      };
    },
  );

  getColumnsWithIndex: (state: {
    columns: ColumnOrRowWithDefinitions[];
    conditionTableRows: ColumnOrRowWithDefinitions[];
    mode: TableBuilderMode;
  }) => ColumnWithIndex[] = createSelector(
    ({ mode }) => mode,
    ({ conditionTableRows }) => conditionTableRows,
    deepEqualSelector(
      ({ columns }) => columns,
      (columns) => columns.map((column) => ({ key: column.key, filter: column.filter, sort: column.sort })),
    ),
    (mode, conditionTableRows) =>
      tableBuilder.getColumnsWithIndex(mode === TableBuilderMode.Condition ? conditionTableRows : this.columns),
  );

  getSimpleTableData: (state: {
    columnsWithIndex: ColumnWithIndex[];
    rawTableData: SimpleTableRow[];
  }) => SimpleTableRow[] = createSelector(
    [({ columnsWithIndex }) => columnsWithIndex, ({ rawTableData }) => rawTableData],
    (columnsWithIndex, rawTableData) => {
      return tableBuilder.processSimpleTableData(rawTableData, this.columns, columnsWithIndex);
    },
  );

  getConditionTableData: (state: {
    columnsWithIndex: ColumnWithIndex[];
    rawTableData: ConditionTableData;
    isTransposed: boolean;
  }) => ConditionTableData = createSelector(
    [
      ({ columnsWithIndex }) => columnsWithIndex,
      ({ rawTableData }) => rawTableData,
      ({ isTransposed }) => isTransposed,
    ],
    (columnsWithIndex, rawTableData, isTransposed) => {
      return {
        headers: rawTableData.headers,
        capsules: tableBuilder.processConditionTableData({
          isTransposed,
          data: rawTableData.capsules,
          columnsWithIndex,
          conditionTableRows: this.conditionTableColumns.rows,
          tableItems: this.getTableItemsProcess(),
        }),
      };
    },
  );

  /**
   * Get the callback to pass to the formula service, that creates condition formulas from table items.
   * Used for computing the Condition Table.
   *
   * @param items - list of items in the table
   * @param itemColumnsMap - map between metrics and the multiple columns they correspond to in the computed table
   * @param conditionPropertyColumns - The columns that must use a parameter expression in order to get the
   * property from the backing condition
   * @param isRunAcrossAssets - True if it formula is being run across assets
   * @param statColumns - All statistic columns being used
   * @returns callback that gets the condition formulas for conditions/metrics
   */
  getBuildConditionFormulaFunction(
    items: TableBuilderItem[],
    itemColumnsMap: ItemColumnsMap,
    conditionPropertyColumns: ConditionPropertyColumnDefinition[],
    isRunAcrossAssets: boolean,
    statColumns: StatColumn[],
  ): BuildConditionFormulaCallback {
    const idOfItemInAssetTree: string | undefined =
      _.find(items, (item) => !_.isEmpty(item.assets))?.id ?? _.find(statColumns, 'signalId')?.signalId;
    return (ids, parameters) => {
      const idToShortName = _.invert(parameters);
      const formula = _.chain(ids)
        .map((id) => {
          const identifier = idToShortName[id];
          const formulaParts = [`$${identifier}`];
          const targetItem = _.find(items, { id }) as TableBuilderItem | undefined;
          if (targetItem?.itemType === ITEM_TYPES.METRIC) {
            const columns = _.values(itemColumnsMap[id]);
            formulaParts.push('toCondition()');
            formulaParts.push(
              ...(_.chain(columns)
                .filter('metricProperty')
                .map(
                  (column: MetricPropertyColumn) =>
                    `renameProperty('${column.metricProperty}', '${column.propertyName}')`,
                )
                .value() as unknown as string[]),
            );
            formulaParts.push(
              ...(_.chain(columns)
                .filter('expression')
                .map((column: MetricPropertyColumn) => {
                  const propIdentifier = `${identifier}_${column.key.split('_')[1]}`;
                  parameters[propIdentifier] = `$${identifier}.${column.expression}`;
                  return `setProperty('${column.propertyName}', $${propIdentifier})`;
                })
                .value() as unknown as string[]),
            );
          }

          formulaParts.push(
            ..._.map(conditionPropertyColumns, (column, index) => {
              // If going across assets and the condition is not in the asset tree then find another item that is in
              // the asset tree so the user can see from which asset the statistic came (CRAB-33710).
              let conditionIdentifier = identifier;
              if (
                column.key === COLUMNS_AND_STATS.asset.key &&
                isRunAcrossAssets &&
                idOfItemInAssetTree &&
                _.isEmpty(targetItem?.assets)
              ) {
                if (idToShortName[idOfItemInAssetTree]) {
                  conditionIdentifier = idToShortName[idOfItemInAssetTree];
                } else {
                  conditionIdentifier = `${getShortIdentifier(index)}_assetItem`;
                  parameters[conditionIdentifier] = idOfItemInAssetTree;
                }
              }
              const propertyIdentifier = `${conditionIdentifier}_conditionProp_${getShortIdentifier(index)}`;
              const expression = column.propertyExpression ?? `property('${column.key}')`;
              parameters[propertyIdentifier] = `$${conditionIdentifier}.${expression}`;
              return `setProperty('${column.key}', $${propertyIdentifier})`;
            }),
          );

          return formulaParts.join('.');
        })
        .join(', ')
        .value();
      return { formula, parameters };
    };
  }

  /**
   * Get the callback to pass to the formula service, that creates formulas for signal statistic columns
   * Used for computing the Condition Table.
   *
   * @param statColumns - list of statistic columns in the table
   * @returns callback that gets the condition formulas for conditions/metrics
   */
  getBuildStatFormulaFunction(statColumns: ColumnOrRowWithDefinitions[]): BuildStatFormulaCallback {
    const isRunAcrossAssets = !!this.getAssetId();
    const isHomogenizeUnits = this.getIsHomogenizeUnits();
    const itemTransformer = (itemIdentifier: string, signalId: string, parameters: ParametersMap) => {
      if (isRunAcrossAssets) {
        const item = sqTrendSeriesStore.findItem(signalId);
        if (!isStringSeriesUtil(item)) {
          const mapIdsToShortIdentifiers = _.invert(parameters);
          const itemIdentifier = mapIdsToShortIdentifiers[signalId];
          if (isHomogenizeUnits) {
            const fixedItemUOMIdentifier = `fixed_${itemIdentifier}_${ITEM_UOM}`;
            parameters[fixedItemUOMIdentifier] = `$${signalId}.property('${SeeqNames.Properties.ValueUom}')`;
            return `$${itemIdentifier}.convertUnits($${fixedItemUOMIdentifier})`;
          } else {
            return `$${itemIdentifier}.convertUnits('')`;
          }
        }
      }
      return itemIdentifier;
    };
    return getBuildStatFormulaFunctionCallback(statColumns, itemTransformer);
  }

  /**
   * Get the formula fragment for a property column for a particular item.
   * Used when computing the Simple Table.
   *
   * @param column - the column we're trying to add to the formula
   * @param item - the item we're adding the column for
   * @param itemIdentifier - the short id for the item to use in the formula
   */
  getSimpleTablePropertyColumnFormula(
    column: ColumnOrRowWithDefinitions,
    item: { itemType: ITEM_TYPES },
    itemIdentifier: string,
  ): string {
    const assetPathLevel = column.assetPathLevel ? column.assetPathLevel.toString() : '0';
    return _.cond([
      [
        _.matches({ key: COLUMNS_AND_STATS.asset.key }),
        () => `$${itemIdentifier}.parentProperty('${SeeqNames.Properties.Name}')`,
      ],
      [_.matches({ key: 'fullpath' }), () => `$${itemIdentifier}.ancestors(' >> ')`],
      [
        (column: ColumnOrRowWithDefinitions) => Boolean(column.key?.startsWith(ASSET_PATH_COLUMN_PREFIX)),
        () => `$${itemIdentifier}.assetPath('${assetPathLevel}')`,
      ],
      [
        (column: ColumnOrRowWithDefinitions) => Boolean(column.key?.startsWith(ASSET_PATH_ID_COLUMN_PREFIX)),
        () => `$${itemIdentifier}.assetPathId('${assetPathLevel}')`,
      ],
      [
        _.property('propertyName'),
        ({ propertyName }) =>
          `$${itemIdentifier}.property('${_.isFunction(propertyName) ? propertyName(item.itemType) : propertyName}')`,
      ],
      [_.matches({ type: TableBuilderColumnType.Property }), ({ key }) => `$${itemIdentifier}.property('${key}')`],
      [
        _.stubTrue,
        ({ key }) => {
          throw new TypeError(`${key} column is not supported in simple table formula`);
        },
      ],
    ])(column);
  }

  /**
   * Custom item/capsule properties used in the table.
   */
  getPropertyColumns: (columns: ColumnOrRowWithDefinitions[]) => PropertyColumn[] = createSelector(
    (columns) => columns,
    (columns) => tableBuilder.getPropertyColumns(columns),
  );

  /**
   * Finds the index of a column
   * @param key - Column key
   * @param isNotFoundAllowed - If true, does not throw if the column does not exist
   * @returns the index of the column. If the column is not found, it returns -1
   */
  getColumnIndex(key: string | number, isNotFoundAllowed = false): number {
    return tableBuilder.getColumnIndex(this.getColumns(), key as string, isNotFoundAllowed);
  }

  /**
   * Returns the column with the specified key.
   * @param key - Column key
   *
   * @returns the column with the specified key.
   */
  getColumn(key: string | number): ColumnOrRow {
    return _.find(this.state.get(['columns', this.state.get('mode')]), {
      key,
    }) as ColumnOrRow;
  }

  /**
   * Returns the column definition with the specified key.
   * @param key - Column key
   *
   * @returns the column definition with the specified key.
   */
  getColumnDefinition(key: string): ColumnOrRowWithDefinitions {
    return this.columns.find((column) => column.key === key)!;
  }

  /**
   * Dehydrates the item by retrieving the current set parameters in view
   * @returns {Object} An object with the state properties as JSON
   */
  dehydrate(): TableBuilderDehydratedState {
    return _.omit(this.state.serialize(), [
      'tableData',
      'columnIdToAutoHeight',
      'clipboardStyle',
      'distinctStringValueMap',
      'fetchFailedMessage',
      'tableFetchAttempted',
    ]);
  }

  /**
   * Rehydrates item from dehydrated state
   *
   * @param {Object} dehydratedState - State object that should be restored
   */
  rehydrate(dehydratedState: Partial<TableBuilderDehydratedState>): void {
    this.state.deepMerge(dehydratedState);
  }

  protected readonly handlers = {
    SIMPLE_THRESHOLD_METRIC_CREATED: this.handleSimpleMetricCreated,
    TABLE_BUILDER_SET_MODE: this.setMode,
    TABLE_BUILDER_ADD_COLUMN: this.addColumn,
    TABLE_BUILDER_ADD_METRIC_TO_CONDITION_TABLE: this.addMetricColumnToConditionTable,
    TABLE_BUILDER_ADD_METRIC_TO_FORMULA_ACROSS_CONDITION_TABLE: this.addMetricColumnForFormulaAcrossConditionTable,
    TABLE_BUILDER_REMOVE_COLUMN: this.removeColumn,
    TABLE_BUILDER_MOVE_COLUMN: this.moveColumn,
    TABLE_BUILDER_SET_COLUMN_BACKGROUND: this.setColumnBackground,
    TABLE_BUILDER_SET_COLUMN_TEXT_ALIGN: this.setColumnTextAlign,
    TABLE_BUILDER_SET_COLUMN_TEXT_COLOR: this.setColumnTextColor,
    TABLE_BUILDER_SET_COLUMN_TEXT_STYLE: this.setColumnTextStyle,
    TABLE_BUILDER_SET_HEADER_BACKGROUND: this.setHeaderBackground,
    TABLE_BUILDER_SET_HEADER_TEXT_ALIGN: this.setHeaderTextAlign,
    TABLE_BUILDER_SET_HEADER_TEXT_COLOR: this.setHeaderTextColor,
    TABLE_BUILDER_SET_HEADER_TEXT_STYLE: this.setHeaderTextStyle,
    TABLE_BUILDER_SET_STYLE_TO_ALL_COLUMNS: this.setStyleForAllColumns,
    TABLE_BUILDER_SET_STYLE_TO_ALL_HEADERS: this.setStyleForAllHeaders,
    TABLE_BUILDER_SET_STYLE_TO_ALL_HEADERS_AND_COLUMNS: this.setStyleForAllHeadersAndColumns,
    TABLE_BUILDER_COPY_STYLE: this.copyStyle,
    TABLE_BUILDER_PASTE_STYLE_ON_HEADER: this.pasteStyleOnHeader,
    TABLE_BUILDER_PASTE_STYLE_ON_COLUMN: this.pasteStyleOnColumn,
    TABLE_BUILDER_PASTE_STYLE_ON_HEADER_AND_COLUMN: this.pasteStyleOnHeaderAndColumn,
    TABLE_BUILDER_SET_COLUMN_FILTER: this.setColumnFilter,
    TABLE_BUILDER_SORT_BY_COLUMN: this.sortByColumn,
    TABLE_BUILDER_SET_CELL_TEXT: this.setCellText,
    TABLE_BUILDER_SET_COLUMN_WIDTH: this.setColumnWidth,
    TABLE_BUILDER_SET_HEADER_TEXT: this.setHeaderText,
    TABLE_BUILDER_SET_HEADERS_TYPE: this.setHeadersType,
    TABLE_BUILDER_SET_HEADERS_FORMAT: this.setHeadersFormat,
    TABLE_BUILDER_SET_HEADERS_PROPERTY: this.setHeadersProperty,
    TABLE_BUILDER_PUSH_CONDITION_DATA: this.setConditionTableData,
    TABLE_BUILDER_PUSH_SIMPLE_DATA: this.setSimpleTableData,
    TABLE_BUILDER_SET_HEADER_OVERRIDE: this.setHeaderOverridden,
    TABLE_BUILDER_SET_IS_TRANSPOSED: this.setIsTransposed,
    TABLE_BUILDER_SET_ASSET_ID: this.setAssetId,
    TABLE_BUILDER_SET_HOMOGENIZE_UNITS: this.setIsHomogenizeUnits,
    TABLE_BUILDER_SET_IS_MIGRATING: this.setIsMigrating,
    TABLE_BUILDER_SET_IS_TABLE_STRIPED: this.setIsTableStriped,
    TABLE_BUILDER_SET_USE_SIGNAL_COLORS_IN_CHART: this.setUseSignalColorsInChart,
    TABLE_BUILDER_SET_ASSET_PATHS: this.setAssetPaths,
    TABLE_BUILDER_SET_FETCH_FAILED_MESSAGE: this.setFetchFailedMessage,
    TABLE_BUILDER_SET_CHART_VIEW: this.setChartView,
    TABLE_BUILDER_SET_TREE_MAP_VIEW: this.setTreemapView,
    TABLE_BUILDER_SET_CHART_VIEW_SETTINGS: this.setChartViewSettings,
    TABLE_BUILDER_SET_CONDITION_CHART_VIEW: this.setConditionChartView,
    TABLE_BUILDER_SET_TREEMAP_VIEW_SETTINGS: this.setTreemapSettings,
    TABLE_BUILDER_SET_ARE_ALL_ROWS_EXPANDED: this.setAreAllRowsExpanded,
    TABLE_BUILDER_SET_CHART_VIEW_CONDITION_SETTINGS: this.setChartViewConditionSettings,
    TABLE_BUILDER_SET_CHART_MENU_SETTINGS: this.setChartMenuSettings,
    TABLE_BUILDER_SET_SIMPLE_DISTINCT_STRING_VALUES: this.setSimpleDistinctStringValues,
    TABLE_BUILDER_SET_CONDITION_DISTINCT_STRING_VALUES: this.setConditionDistinctStringValues,
    TABLE_BUILDER_REMOVE_ITEMS: this.removeItems,
    TREND_SET_PANEL_SORT: this.setSortedTableData,
    TREND_REMOVE_ITEMS: this.removeItems,
    TREND_SWAP_ITEMS: this.swapItems,
    TABLE_BUILDER_TOGGLE_ROW_GROUPING: this.toggleRowGrouping,
    TABLE_BUILDER_GROUP_BY_ROW: this.enableRowGroup,
    TABLE_BUILDER_UNGROUP_ROW: this.disableRowGroup,
    TABLE_BUILDER_SET_ROW_GROUP_SHOW: this.setRowGroupShow,
    TABLE_BUILDER_SET_ROW_GROUP_ORDER: this.setRowGroupOrder,
    TABLE_BUILDER_SET_AGGREGATION_FUNCTION: this.setAggregationFunction,
    TABLE_BUILDER_SET_ITEM_FINDER_ID: this.setItemFinderId,
    TABLE_BUILDER_SET_TABLE_DEFINITION_ID: this.setTableDefinitionId,
    TABLE_BUILDER_PUSH_FORMULA_ACROSS_SIMPLE_TABLE_DATA: this.setFormulaAcrossSimpleTableData,
    TABLE_BUILDER_REFRESH_FORMULA_ACROSS_TABLE_FORMATTING: this.refreshFormulaAcrossTableFormatting,
    TABLE_BUILDER_REFRESH_CONDITION_TABLE_FORMATTING: this.refreshConditionTableFormatting,
    TABLE_BUILDER_REFRESH_SIMPLE_TABLE_FORMATTING: this.refreshSimpleTableFormatting,
    TABLE_BUILDER_SET_IS_FORMULA_ACROSS_TABLE: this.setIsFormulaAcrossTable,
    TABLE_BUILDER_SET_IS_FORMULA_ACROSS_TABLE_PROCESSING: this.setIsFormulaAcrossTableProcessing,
    TABLE_BUILDER_SET_ITEM_FINDER_INPUT: this.setItemFinderInput,
    TABLE_BUILDER_SET_TABLE_FETCH_ATTEMPTED: () => {
      this.state.set('tableFetchAttempted', true);
    },
    TABLE_BUILDER_SET_ASSET_PATH_STATE_HASH: this.setAssetPathStateHash,
  };

  /**
   * Sets the mode of the table builder
   *
   * @param payload - Object container for arguments
   * @param payload.mode - The mode
   */
  setMode(payload: { mode: TableBuilderMode }) {
    this.state.set('mode', payload.mode);
  }

  /**
   * Adds a new table column.
   *
   * @param {Object} payload - Object container. Can either be a special type or one of the predefined columns.
   * @param {TableBuilderColumnType} [payload.type] - The column type
   * @param {string} [payload.assetPathId] - The asset path id, if specified
   * @param {number} [payload.assetPathLevel] - The asset path level, if specified
   * @param {string} [payload.style] - The column style for property columns
   * @param {string} [payload.propertyName] - The property name, required if type is TableBuilderColumnType.Property
   * @param {PropertyColumn} [payload.column] - One of the predefined columns
   */
  addColumn(payload: {
    type?: TableBuilderColumnType;
    assetPathId?: string;
    assetPathLevel?: number;
    style?: string;
    propertyName?: string;
    column?: PropertyColumn | StatisticColumn | TableColumn;
    signalId?: string;
    metricId?: string;
    modeOverride?: TableBuilderMode;
  }) {
    const columnDefinition = withDefaultFormatting(
      _.cond([
        [_.matches({ type: TableBuilderColumnType.Text }), () => ({ key: base64guid(), type: payload.type })],
        [
          _.matches({ type: TableBuilderColumnType.AssetPath }),
          () => ({
            key: `${ASSET_PATH_COLUMN_PREFIX}${base64guid()}`,
            type: TableBuilderColumnType.AssetPath,
            assetPathId: payload.assetPathId,
            assetPathLevel: payload.assetPathLevel,
            backingTableColumn: `${SEARCH_ANCESTOR_COLUMN_NAME} ${payload.assetPathLevel}.properties.name`,
            style: 'string',
          }),
        ],
        [
          (p) => isPropertyColumnType(p as ColumnOrRow),
          () => ({
            key: payload.propertyName!,
            type: payload.type,
            style: payload.style,
            backingTableColumn: `item id.properties.${payload.propertyName}`,
          }),
        ],
        [
          (p: typeof payload) => Boolean(p.signalId),
          () => ({
            // Can add the same statistic for many series, so a unique key must be created
            key: this.getColumnKeyFromSignalId(payload.column!, payload.signalId),
            statisticKey: payload.column!.key,
            signalId: payload.signalId,
          }),
        ],
        [
          _.property('metricId'),
          () => ({
            key: payload.metricId!,
            metricId: payload.metricId,
          }),
        ],
        [
          _.property('column'),
          // column key is enough for a predefined column. We don't want to store and persist unnecessary attributes
          () => ({ key: payload.column!.key! }),
        ],
        [
          _.stubTrue,
          () => {
            throw new TypeError(`Unknown column type ${payload}`);
          },
        ],
      ])(payload),
    );

    if (!this.isColumnKeyEnabled(columnDefinition.key)) {
      const mode = payload.modeOverride ?? this.state.get('mode');
      const cursor = this.state.select('columns', mode);
      // check if we should add the column to a predefined position
      if (PREDEFINED_COLUMN_INDEX[columnDefinition.key] >= 0) {
        cursor.splice([PREDEFINED_COLUMN_INDEX[columnDefinition.key], 0, columnDefinition]);
      } else {
        cursor.push(columnDefinition);
        if (typeof columnDefinition?.key === 'string' && columnDefinition.key.includes(ASSET_PATH_COLUMN_PREFIX)) {
          cursor.push({
            ...columnDefinition,
            key: columnDefinition.key.replace(ASSET_PATH_COLUMN_PREFIX, ASSET_PATH_ID_COLUMN_PREFIX),
            // the assetPathId column uses a different backing column
            backingTableColumn: `${SEARCH_ANCESTOR_COLUMN_NAME} ${payload.assetPathLevel}`,
          });
        }
      }
    }
  }

  addMetricColumnToConditionTable(payload: { metricId: string }) {
    this.addColumn({ ...payload, modeOverride: TableBuilderMode.Condition });
  }

  addMetricColumnForFormulaAcrossConditionTable(payload: { metricId: string }) {
    if (!this.getColumn(payload.metricId)) {
      this.addColumn({ ...payload, modeOverride: TableBuilderMode.Condition });
    }
    const column = this.getColumn(payload.metricId);
    this.state.set(['columns', TableBuilderMode.Condition, this.getColumnIndex(payload.metricId)], {
      ...column,
      ...payload,
    });
  }

  /**
   * Removes the specified table column.
   *
   * @param {Object} payload - Object container
   * @param {string} payload.key - The key that identifies the column
   */
  removeColumn({ key }: { key: string }) {
    const columnIndex = this.getColumnIndex(key);
    const column = this.getColumn(key);
    // remove sort first so that we can update the sort level of the remaining columns
    this.removeSort(key);
    // Collect all column indices to remove
    const indicesToRemove = [columnIndex];
    if (key.includes(ASSET_PATH_COLUMN_PREFIX)) {
      // If the column is an assetPath, we also remove the assetPathId column
      const assetPathIdKey = key.replace(ASSET_PATH_COLUMN_PREFIX, ASSET_PATH_ID_COLUMN_PREFIX);
      const assetPathIdIndex = this.getColumnIndex(assetPathIdKey);
      if (assetPathIdIndex > -1) {
        indicesToRemove.push(assetPathIdIndex);
      }
    }

    // Remove all columns in a single state update
    const currentColumns = this.state.get(['columns', this.state.get('mode')]);
    const updatedColumns = currentColumns.filter((_: any, idx: number) => !indicesToRemove.includes(idx));
    this.state.set(['columns', this.state.get('mode')], updatedColumns);

    if (this.getIsSimpleMode()) {
      const currentData = this.state.get('tableData', TableBuilderMode.Simple);
      this.state.set(
        ['tableData', TableBuilderMode.Simple],
        currentData.map((row: any) => ({
          ...row,
          cells: row.cells.filter((_: any, idx: number) => !indicesToRemove.includes(idx)),
        })),
      );
    } else {
      if (isPropertyOrStatOrMetricColumn(column)) {
        const dataIndex = _.findIndex(this.state.get(['tableData', TableBuilderMode.Condition, 'headers']), {
          key: column.key,
        });
        const currentTableData = this.state.get('tableData', TableBuilderMode.Condition);
        this.state.set(['tableData', TableBuilderMode.Condition], {
          headers: currentTableData.headers.filter((_: any, idx: number) => idx !== dataIndex),
          capsules: currentTableData.capsules.map((capsule: any) => ({
            ...capsule,
            values: capsule.values.filter((_: any, idx: number) => idx !== dataIndex),
          })),
        });
      }
    }
  }

  /**
   * Moves the specified table column to a new position.
   *
   * @param {Object} payload - Object container
   * @param {string} payload.key - The key that identifies the column
   * @param {string} payload.newKey - The key that specifies the column of the new position
   */
  moveColumn(payload: { key: string; newKey: string }) {
    const cursor = this.state.select('columns', this.state.get('mode'));
    const index = this.getColumnIndex(payload.key);
    const newIndex = this.getColumnIndex(payload.newKey);
    const column = cursor.get(index);
    cursor.splice([index, 1]);
    cursor.splice([newIndex, 0, column]);
    if (this.getIsSimpleMode()) {
      _.forEach(this.state.get('tableData', TableBuilderMode.Simple), (row, rowIndex) => {
        const cellCursor = this.state.select('tableData', TableBuilderMode.Simple, rowIndex, 'cells');
        const cellValue = cellCursor.get(index);
        cellCursor.splice([index, 1]);
        cellCursor.splice([newIndex, 0, cellValue]);
      });
    } else if (isPropertyOrStatOrMetricColumn(column)) {
      const headerCursor = this.state.select('tableData', TableBuilderMode.Condition, 'headers');
      const headerIndex = _.findIndex(headerCursor.get(), {
        key: payload.key,
      });
      const newHeaderIndex = _.findIndex(headerCursor.get(), {
        key: payload.newKey,
      });
      const headerValue = headerCursor.get(headerIndex);
      headerCursor.splice([headerIndex, 1]);
      headerCursor.splice([newHeaderIndex, 0, headerValue]);
      _.forEach(this.state.get('tableData', TableBuilderMode.Condition, 'capsules'), (row, rowIndex) => {
        const valueCursor = this.state.select('tableData', TableBuilderMode.Condition, 'capsules', rowIndex, 'values');
        const value = valueCursor.get(headerIndex);
        valueCursor.splice([headerIndex, 1]);
        valueCursor.splice([newHeaderIndex, 0, value]);
      });
    }
  }

  /**
   * Sets the background color for a table column (header excluded).
   *
   * @param {Object} payload - Object container
   * @param {string} payload.key - The key of the column
   * @param {String} payload.color - Background color for the column
   */
  setColumnBackground(payload: { key: string | number; color: string }) {
    this.state.set(
      ['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'backgroundColor'],
      payload.color,
    );
  }

  setColumnTextAlign(payload: { key: string | number; align: string }) {
    this.state.set(['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'textAlign'], payload.align);
  }

  setColumnTextColor(payload: { key: string | number; color: string }) {
    this.state.set(['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'textColor'], payload.color);
  }

  setColumnTextStyle(payload: { key: string; style: string[] }) {
    this.state.set(['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'textStyle'], payload.style);
  }

  /**
   * Sets the background color for a table header.
   *
   * @param {Object} payload - Object container
   * @param {string} payload.key - The key of the column
   * @param {String} payload.color - Background color for the header
   */
  setHeaderBackground(payload: { key: string | number; color: string }) {
    this.state.set(
      ['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'headerBackgroundColor'],
      payload.color,
    );
  }

  setHeaderTextAlign(payload: { key: string; align: string }) {
    this.state.set(
      ['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'headerTextAlign'],
      payload.align,
    );
  }

  setHeaderTextColor(payload: { key: string | number; color: string }) {
    this.state.set(
      ['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'headerTextColor'],
      payload.color,
    );
  }

  setHeaderTextStyle(payload: { key: string; style: string[] }) {
    this.state.set(
      ['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'headerTextStyle'],
      payload.style,
    );
  }

  setStyleForAllColumns(payload: { key: string | number }) {
    const sourceColumn = this.getColumn(payload.key);
    _.forEach(this.getColumns(), (column, index) => {
      this.state.merge(['columns', this.state.get('mode'), index], {
        backgroundColor: sourceColumn.backgroundColor,
        textAlign: sourceColumn.textAlign,
        textColor: sourceColumn.textColor,
        textStyle: sourceColumn.textStyle,
      });
    });
  }

  setStyleForAllHeaders(payload: { key: string | number }) {
    const sourceColumn = this.getColumn(payload.key);
    _.forEach(this.getColumns(), (column, index) => {
      this.state.merge(['columns', this.state.get('mode'), index], {
        headerBackgroundColor: sourceColumn.headerBackgroundColor,
        headerTextAlign: sourceColumn.headerTextAlign,
        headerTextColor: sourceColumn.headerTextColor,
        headerTextStyle: sourceColumn.headerTextStyle,
      });
    });
  }

  setStyleForAllHeadersAndColumns(payload: { key: string | number }) {
    this.setStyleForAllColumns(payload);
    this.setStyleForAllHeaders(payload);
  }

  copyStyle(payload: { key: string | number }) {
    const sourceColumn = this.getColumn(payload.key);
    this.state.set('clipboardStyle', {
      backgroundColor: sourceColumn.backgroundColor,
      textAlign: sourceColumn.textAlign,
      textColor: sourceColumn.textColor,
      textStyle: sourceColumn.textStyle,
      headerBackgroundColor: sourceColumn.headerBackgroundColor,
      headerTextAlign: sourceColumn.headerTextAlign,
      headerTextColor: sourceColumn.headerTextColor,
      headerTextStyle: sourceColumn.headerTextStyle,
    });
  }

  pasteStyleOnHeader(payload: { key: string | number }) {
    const clipboardStyle = this.state.get('clipboardStyle');
    this.state.merge(['columns', this.state.get('mode'), this.getColumnIndex(payload.key)], {
      headerBackgroundColor: clipboardStyle.headerBackgroundColor,
      headerTextAlign: clipboardStyle.headerTextAlign,
      headerTextColor: clipboardStyle.headerTextColor,
      headerTextStyle: clipboardStyle.headerTextStyle,
    });
  }

  pasteStyleOnColumn(payload: { key: string | number }) {
    const clipboardStyle = this.state.get('clipboardStyle');
    this.state.merge(['columns', this.state.get('mode'), this.getColumnIndex(payload.key)], {
      backgroundColor: clipboardStyle.backgroundColor,
      textAlign: clipboardStyle.textAlign,
      textColor: clipboardStyle.textColor,
      textStyle: clipboardStyle.textStyle,
    });
  }

  pasteStyleOnHeaderAndColumn(payload: { key: string | number }) {
    this.pasteStyleOnColumn(payload);
    this.pasteStyleOnHeader(payload);
  }

  /**
   * Set (or unset) a filter on a column. If payload.filter is undefined, any existing filter is removed.
   *
   * @param payload - object container for args
   * @param payload.key - column key (simple or condition property/stat column) or item id (condition metric)
   * @param payload.filter - column filter (can be undefined)
   */
  setColumnFilter(payload: { key: string; filter: TableColumnFilter }) {
    if (payload.filter) {
      this.state.set(['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'filter'], payload.filter);
    } else {
      this.state.unset(['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'filter']);
    }
  }

  /**
   * Set (or unset) a sort criterion on a column. If payload.direction is 'none', the sort criterion is removed.
   * @param payload - object container for args
   * @param payload.key - column key
   * @param payload.direction - column sort order
   */
  sortByColumn(payload: { key: string; direction?: string }) {
    if (_.isUndefined(payload.direction)) {
      this.removeSort(payload.key);
    } else {
      this.addOrUpdateSort(payload.key, payload.direction);
    }
  }

  /**
   * Set or update the sort criterion on the specified column. When sorting on multiple levels, the last column
   * added is the most important.
   * @param key - column key or itemId
   * @param direction - column sort order
   */
  addOrUpdateSort(key: string, direction: string) {
    const columnIndex = this.getColumnIndex(key);
    if (_.has(this.getColumn(key), 'sort')) {
      this.state.set(['columns', this.state.get('mode'), columnIndex, 'sort', 'direction'], direction);
    } else {
      this.incrementSortLevels();
      this.state.set(['columns', this.state.get('mode'), columnIndex, 'sort'], { direction, level: 1 });
    }
  }

  /**
   * Increments the sort level on all sorts
   */
  incrementSortLevels() {
    _.chain(this.getColumns())
      .filter((column) => !!column.sort?.level)
      .forEach((column) => {
        const index = this.getColumnIndex(column.key);
        this.state.set(['columns', this.state.get('mode'), index, 'sort', 'level'], column.sort!.level + 1);
      })
      .value();
  }

  /**
   * Removes the sort criterion from the specified column.
   * @param key - column key or itemId
   */
  removeSort(key: string) {
    // we may not have a sort level. This function is also called from removeColumn
    const sortLevel: number = this.getColumn(key)?.sort?.level ?? Infinity;
    const maybeColumn = this.getColumn(key);
    if (_.isUndefined(maybeColumn)) {
      return;
    }
    const columnIndex = this.getColumnIndex(key);
    this.state.unset(['columns', this.state.get('mode'), columnIndex, 'sort']);
    _.forEach(this.getColumns(), (column, index) => {
      if (column.sort?.level !== undefined && column.sort.level > sortLevel) {
        this.state.set(['columns', this.state.get('mode'), index, 'sort', 'level'], column.sort.level - 1);
      }
    });
  }

  /**
   * Sets the text for a table column cell.
   *
   * @param {Object} payload - Object container
   * @param {Number} payload.key - Column key
   * @param {String} payload.text - Text for the cell
   * @param {String} [payload.cellKey] - The identifier for the cell. If not specified the column header text
   * will be set.
   */
  setCellText(payload: { key: string; text: string; cellKey?: string }) {
    const index = this.getColumnIndex(payload.key);
    if (
      payload.cellKey &&
      this.state.get(['columns', this.state.get('mode'), index, 'type']) !== TableBuilderColumnType.Text
    ) {
      throw new TypeError('Can only set text on a column of type text');
    }

    if (payload.cellKey) {
      this.state.set(['columns', this.state.get('mode'), index, 'cells', payload.cellKey], _.trim(payload.text));
    } else {
      this.state.set(['columns', this.state.get('mode'), index, 'header'], _.trim(payload.text));
    }
  }

  setColumnWidth({ key, newWidth }: { key: string; newWidth: number | undefined }) {
    const mode = this.mode;
    const index = this.getColumnIndex(key, true);
    const path = index === -1 ? ['otherColumns', mode, key] : ['columns', mode, index];
    const columnCursor = this.state.select(path);

    if (newWidth === undefined) {
      columnCursor.unset('width');
    } else {
      columnCursor.set('width', newWidth);
    }

    if (_.isEmpty(columnCursor.get())) {
      columnCursor.unset();
    }
  }

  /**
   * Sets the header override flag for a column and disables the overridden flag for other columns.
   *
   * @param payload - Object container
   * @param payload.columnKey - The column key.
   */
  setHeaderOverridden({ columnKey }: { columnKey: string }) {
    _.forEach(this.state.get('columns', this.state.get('mode')), (column, columnIndex) => {
      if (columnKey === column.key) {
        this.state.set(['columns', this.state.get('mode'), columnIndex, 'headerOverridden'], true);
      } else {
        this.state.unset(['columns', this.state.get('mode'), columnIndex, 'headerOverridden']);
      }
    });
  }

  /**
   * Sets the text for a table column header.
   *
   * @param {Object} payload - Object container
   * @param {Number} payload.columnKey - Column key
   * @param {String} payload.text - Text for the header
   */
  setHeaderText(payload: { columnKey: string; text: string }) {
    const columnIndex = this.getColumnIndex(payload.columnKey);
    this.state.set(['columns', this.state.get('mode'), columnIndex, 'header'], _.trim(payload.text));
  }

  /**
   * Sets the header type for table columns that display static headers (all columns in condition mode, and the name
   * column in simple mode).
   *
   * @param {Object} payload - Object container
   * @param {TableBuilderHeaderType} payload.type - The type of header to display
   */
  setHeadersType(payload: { type: TableBuilderHeaderType }) {
    this.state.set(['headers', this.state.get('mode'), 'type'], payload.type);
  }

  /**
   * Sets the date format used for headers of static headers (all columns in condition mode, and the name column
   * in simple mode).
   *
   * @param {Object} payload - Object container
   * @param {String} payload.format - A string that can be passed to moment's format()
   */
  setHeadersFormat(payload: { format: string }) {
    this.state.set(['headers', this.state.get('mode'), 'format'], payload.format);
  }

  /**
   * Sets the name of the capsule property used for headers of metric value columns when the type is CapsuleProperty.
   *
   * @param {Object} payload - Object container
   * @param {String} payload.property - The capsule property name
   */
  setHeadersProperty(payload: { property: string }) {
    if (_.isEmpty(payload.property)) {
      this.setHeadersType({ type: TableBuilderHeaderType.StartEnd });
      this.state.unset(['headers', this.state.get('mode'), 'property']);
    } else {
      this.state.set(['headers', this.state.get('mode'), 'property'], payload.property);
    }
  }

  /**
   * Sorts the table data and sets it in the store.
   */
  setSortedTableData() {
    // We only need the value of the sort flag here, so just wait for 'sqTrendStore'.
    // The items are already in their store.
    this.waitFor(['sqTrendStore'], () => {
      if (!this.getIsSimpleMode()) {
        const itemIds = _.map(this.getTableItemsProcess(), 'id');
        const sortByItemIndex = (field: string) => (cell: Record<string, unknown>) => {
          const index = _.indexOf(itemIds, cell[field]);
          return index === -1 ? itemIds.length : index;
        };
        this.state.set(
          ['tableData', TableBuilderMode.Condition, 'headers'],
          _.sortBy(this.state.get('tableData', TableBuilderMode.Condition, 'headers'), [sortByItemIndex('key')]),
        );
        _.forEach(this.state.get('tableData', TableBuilderMode.Condition, 'capsules'), (capsules, i) => {
          this.state.set(
            ['tableData', TableBuilderMode.Condition, 'capsules', i, 'values'],
            _.sortBy(capsules.values, [sortByItemIndex('formulaItemId')]),
          );
        });
      } else {
        const tableData = this.maybeApplyDefaultSort(
          this.state.get('tableData', TableBuilderMode.Simple) as SimpleTableRow[],
          this.getTableItemsProcess(),
          this.getColumns(),
        );
        this.state.set(['tableData', TableBuilderMode.Simple], tableData);
      }
    });
  }

  /**
   * Removes metric information from all columns. Used to clear metric information before pushing new data into
   * the table.
   */
  removeAllMetricInfo() {
    const columnCount = this.getColumns().length;
    for (let i = 0; i < columnCount; i++) {
      this.state.unset(['columns', this.state.get('mode'), i, 'metrics']);
    }
  }

  /**
   * Builds the simple table data for presentation using the data returned from the API and the map of column
   * positions.
   *
   * @param tableAndColumnPositions - Array of tuples for result tables and column positions for each item type,
   * @param idsToFilterOut - Array of item ids to filter out
   * @param items - Array of items that are being displayed in the table
   * #getSimpleTableFormulaAndParameters()
   */
  setSimpleTableData({
    tableAndColumnPositions,
    idsToFilterOut,
    items,
  }: {
    tableAndColumnPositions: Array<[SimpleTableResultTable, ColumnPosition[]]>;
    idsToFilterOut: string[];
    items: TableBuilderItem[];
  }) {
    const resultRowIdIndex = 2;
    const columns = this.columns;
    const columnIdToAutoHeight: Record<string, boolean> = {};
    const itemOrder = new Map(_.map(items, (item, index) => [String(item.id), index]));
    const tableDataByIteratingOnDataOnly: SimpleTableRow[] = _.chain(tableAndColumnPositions)
      .flatMap(([table, colPositions]: [SimpleTableResultTable, ColumnPosition[]]) => {
        if (_.isNil(table)) {
          return [] as SimpleTableRow[];
        }
        return _.chain(table.data)
          .flatMap((resultRow) => {
            const item = (_.find(items, { id: resultRow[resultRowIdIndex] }) ||
              _.find(items, { id: _.last(resultRow) as string | undefined })) as TableBuilderItem | undefined;
            if (!item) {
              return [];
            }
            let ignoreRow = false;
            const itemId =
              resultRow[
                _.find(colPositions, {
                  key: SIMPLE_TABLE_ID_COLUMN,
                })!.index
              ];
            const formattedRow: SimpleTableRow = {
              itemId: String(itemId),
              formulaItemId: String(item.id),
              cells: _.map(columns, (column) => {
                const getValue = (index: number): SimpleTableCell => {
                  const formattedValue = formatMetricValue(
                    resultRow[index],
                    normalizeFormatOptions(item.formatOptions),
                    column,
                  );
                  if (
                    (columnIdToAutoHeight[column.key] === undefined || columnIdToAutoHeight[itemId] === undefined) &&
                    formattedValue.includes('\n')
                  ) {
                    columnIdToAutoHeight[column.key] = true;
                    columnIdToAutoHeight[itemId] = true;
                  }
                  const rawValue = resultRow[index];
                  const headerUnit = table.headers[index].units;

                  let priorityColor: string | undefined;

                  if (item.itemType !== ITEM_TYPES.METRIC) {
                    const metricTableData = _.find(
                      tableAndColumnPositions,
                      ([table, columnPositions]) =>
                        table &&
                        columnPositions.length > 0 &&
                        _.some(
                          columnPositions,
                          (columnPosition) => columnPosition.key === SeeqNames.Properties.MeasuredItemId,
                        ),
                    );

                    const [metricTable, metricColumnPositions] = metricTableData || [undefined, []];
                    if (!_.isNil(metricTable)) {
                      const measuredItemIndex = _.find(
                        metricColumnPositions,
                        (columnPosition) => columnPosition.key === SeeqNames.Properties.MeasuredItemId,
                      )!.index;
                      const aggregationFunctionIndex = _.find(
                        metricColumnPositions,
                        (columnPosition) => columnPosition.key === SeeqNames.Properties.AggregationFunction,
                      )!.index;

                      const metricMeasuringThisItem = _.find(
                        metricTable.data,
                        (dataRow) =>
                          dataRow[measuredItemIndex] === itemId &&
                          column.stat &&
                          dataRow[aggregationFunctionIndex] === column.stat,
                      );

                      if (!_.isNil(metricMeasuringThisItem)) {
                        const columnPositionForTheMetricValue = _.find(
                          metricColumnPositions,
                          (metricColumnPosition) => metricColumnPosition.key === COLUMNS_AND_STATS.metricValue.key,
                        );
                        priorityColor = metricMeasuringThisItem[columnPositionForTheMetricValue!.index + 1];
                      }
                    }
                  }

                  const unitIndex = _.find(colPositions, (columnPosition) =>
                    _.includes(columnPosition.key, COLUMNS_AND_STATS.valueUnitOfMeasure.key),
                  )!.index;

                  function determineUnits(
                    headerUnit: string,
                    unitFromData: any,
                    column: ColumnOrRowWithDefinitions,
                    shouldClearUnits: boolean,
                  ): any {
                    if (headerUnit === STRING_UOM) {
                      return undefined;
                    } else if (
                      column.key !== COLUMNS_AND_STATS.metricValue.key &&
                      (headerUnit === undefined ||
                        (headerUnit === '' && shouldClearUnits) ||
                        column.type === TableBuilderColumnType.Property ||
                        column.propertyName ||
                        [
                          COLUMNS_AND_STATS['statistics.percentDuration'].key,
                          COLUMNS_AND_STATS['statistics.percentGood'].key,
                          COLUMNS_AND_STATS['statistics.count'].key,
                          COLUMNS_AND_STATS['statistics.totalDuration'].key,
                        ].includes(column.key))
                    ) {
                      return headerUnit;
                    } else {
                      return unitFromData;
                    }
                  }

                  const isHomogenizeUnits = this.getIsHomogenizeUnits();
                  const isRunAcrossAssets = !!this.getAssetId();
                  const unitFromData = unitIndex !== -1 ? resultRow[unitIndex] : undefined;

                  const units = determineUnits(
                    headerUnit,
                    unitFromData,
                    column,
                    isRunAcrossAssets && !isHomogenizeUnits,
                  );

                  const cell: SimpleTableCell = {
                    value: formattedValue,
                    rawValue,
                    ...(units !== undefined ? { units } : {}),
                    ...(priorityColor ? { priorityColor } : {}),
                  };

                  if (formattedValue !== rawValue) {
                    const normalizedFormat = normalizeFormatOptions(item.formatOptions);
                    if (normalizedFormat?.format) {
                      cell.format = { format: normalizedFormat.format };
                    }
                  }

                  return cell;
                };

                const initialColumnIndex = _.find(colPositions, {
                  key: column.key,
                }) as ColumnPosition | undefined;
                let columnIndex = initialColumnIndex;
                if (!_.isNil(columnIndex?.metricId) && columnIndex.metricId !== String(item.id)) {
                  const matchingIndex = _.find(colPositions, {
                    key: column.key,
                    metricId: String(item.id),
                  }) as ColumnPosition | undefined;
                  if (matchingIndex) {
                    columnIndex = matchingIndex;
                  }
                }
                if (!!column.filter && (_.isNil(columnIndex) || _.isNil(resultRow[columnIndex.index]))) {
                  ignoreRow = true;
                }

                const buildCellFromColumnPosition = (position?: ColumnPosition): SimpleTableCell => {
                  if (!position) {
                    return {
                      value: formatMetricValue(null, normalizeFormatOptions(item.formatOptions), column),
                      rawValue: null,
                    };
                  }

                  if (position.metricId) {
                    const cell = getValue(position.index);
                    const metricIdentifier =
                      column.key === COLUMNS_AND_STATS.metricValue.key && item.itemType === ITEM_TYPES.METRIC
                        ? String(item.id)
                        : String(position.metricId);
                    return {
                      ...cell,
                      priorityColor: cell.priorityColor ?? (resultRow[position.index + 1] as string | undefined),
                      metricId: metricIdentifier,
                    };
                  }

                  if (column.key === COLUMNS_AND_STATS.valueUnitOfMeasure.key) {
                    const uom = resultRow[position.index] as string | undefined;
                    if (uom === STRING_UOM) {
                      return { value: '', rawValue: uom };
                    }

                    if (_.isNil(uom)) {
                      const maybeMetricIndex = _.find(colPositions, {
                        metricId: String(item.id),
                      })?.index;
                      if (!_.isNil(maybeMetricIndex)) {
                        const units = table.headers[maybeMetricIndex].units as string | undefined;
                        if (units) {
                          return { value: units, rawValue: units };
                        }
                      }
                      return { value: undefined, rawValue: undefined };
                    }

                    return { value: uom, rawValue: uom };
                  }

                  if (column.key === COLUMNS_AND_STATS['statistics.totalDuration'].key) {
                    const durationValue = resultRow[position.index] as number;
                    return {
                      value: formatDuration(secondsToMillis(durationValue)),
                      rawValue: durationValue,
                    };
                  }

                  return getValue(position.index);
                };

                return buildCellFromColumnPosition(columnIndex);
              }),
            };
            return ignoreRow ? [] : formattedRow;
          })
          .value();
      })
      .compact()
      .reject((item) => idsToFilterOut.includes(item.formulaItemId))
      .sortBy((row) => itemOrder.get(row.formulaItemId) ?? Number.MAX_SAFE_INTEGER)
      .thru((rows) => this.maybeApplyDefaultSort(rows, this.getTableItemsProcess(), columns))
      .value();

    this.state.set(['columnIdToAutoHeight', TableBuilderMode.Simple], columnIdToAutoHeight);
    this.updateOtherColumns(TableBuilderMode.Simple, tableDataByIteratingOnDataOnly);

    this.state.set('fetchFailedMessage', undefined);
    this.state.set(['tableData', TableBuilderMode.Simple], tableDataByIteratingOnDataOnly);
  }

  /**
   * Returns the data unchanged if we have user sort criteria. Otherwise, sort the data first by the item's position
   * in the details pane and then by asset.
   *
   * @param tableData - The data to sort
   * @param items - The items in the details pane
   * @param columns - The columns in the table
   */
  maybeApplyDefaultSort(
    tableData: SimpleTableRow[],
    items: Array<{ id: string }>,
    columns: ColumnOrRowWithDefinitions[],
  ): SimpleTableRow[] {
    return tableBuilder.getMaxSortLevel(this.columns) > 0
      ? tableData
      : this.sortSimpleDataByItemsAndAsset(tableData, items, columns);
  }

  /**
   * Sorts the data first by the item's position in the details pane and then by asset.
   *
   * @param tableData - The data to sort
   * @param items - The items in the details pane
   * @param columns - The columns in the table
   */
  sortSimpleDataByItemsAndAsset(
    tableData: SimpleTableRow[],
    items: Array<{ id: string }>,
    columns: ColumnOrRowWithDefinitions[],
  ): SimpleTableRow[] {
    const itemIds = _.map(items, 'id');
    const sortByItemIndex = (row: SimpleTableRow) => {
      const index = _.indexOf(itemIds, row.formulaItemId);
      return index === -1 ? itemIds.length : index;
    };

    if (this.getAssetId() || this.isFormulaAcrossTable) {
      const assetIndex = _.findIndex(columns, ({ key }) => key === 'asset' || key === 'fullpath');
      if (assetIndex > -1) {
        return _.sortBy(tableData, [sortByItemIndex, `cells[${assetIndex}].value`]);
      }
    }

    return _.sortBy(tableData, [sortByItemIndex]);
  }

  private isSimpleTableData(data: SimpleTableRow[] | ConditionTableCapsule[]): data is SimpleTableRow[] {
    return 'itemId' in (data[0] ?? {});
  }

  /** Removes persisted columns that are not present in the provided data */
  private updateOtherColumns<T extends TableBuilderMode>(
    mode: T,
    data: T extends TableBuilderMode.Simple ? SimpleTableRow[] : ConditionTableCapsule[],
  ): void {
    const otherColumns = this.otherColumns[mode];
    const columnIds = Object.keys(otherColumns).filter((id) => !TRANSPOSE_HEADER_COLUMNS.includes(id));
    const existingColumns = new Map<string, boolean>();

    const updateExistingColumns = (rowId: string) => {
      if (otherColumns[rowId] && !existingColumns.has(rowId)) {
        existingColumns.set(rowId, true);
      }
    };

    if (this.isSimpleTableData(data)) {
      data.forEach((row) => updateExistingColumns(row.itemId));
    } else {
      data.forEach((row) => updateExistingColumns(row.id));
    }

    const otherColumnsCursor = this.state.select(['otherColumns', mode]);
    columnIds.forEach((id) => {
      if (!existingColumns.get(id)) {
        otherColumnsCursor.unset(id);
      }
    });
  }

  /**
   * Builds the table data for display.
   *
   * @param headers - The headers for each column
   * @param table - The capsules and their values
   * @param itemColumnsMap - A mapping of which columns go to which items
   * @param customPropertyName - The name of a custom property on each capsule
   */
  setConditionTableData({
    headers: rawHeaders,
    table,
    itemColumnsMap,
    customPropertyName,
  }: {
    headers: any[];
    table: CapsuleFormulaTableRow[];
    itemColumnsMap: ItemColumnsMap;
    customPropertyName: undefined | string;
  }) {
    if (table.length === MAX_CONDITION_TABLE_CAPSULES) {
      table.pop();
      this.state.set('hasMoreData', true);
    } else if (this.hasMoreData) {
      this.state.set('hasMoreData', false);
    }

    type AdornedColumn = ColumnOrRowWithDefinitions & {
      item: TableBuilderItem | undefined;
      isStartOrEndColumn: boolean;
      formatOption?: { format: string };
    };
    const columns: AdornedColumn[] = this.conditionTableColumns.rows.map((column) => {
      const potentialItemId = column.signalId ?? column.metricId;
      let itemFinder: (id: string | number) => any;
      if (column.signalId) {
        itemFinder = sqTrendSeriesStore.findItem;
      } else if (!this.isSimpleMode() && this.isFormulaAcrossTable) {
        itemFinder = () => (column as any).item;
      } else {
        itemFinder = sqTrendMetricStore.findItem;
      }
      const potentialItem = (potentialItemId ? itemFinder?.(potentialItemId) : undefined) as
        | TableBuilderItem
        | undefined;
      const normalizedItemFormat = normalizeFormatOptions(potentialItem?.formatOptions);
      const formatOption =
        normalizedItemFormat.format !== undefined ? { format: normalizedItemFormat.format } : column.formatOption;
      return {
        ...column,
        formatOption,
        item: potentialItem,
        isStartOrEndColumn: _.includes([COLUMNS_AND_STATS.startTime.key, COLUMNS_AND_STATS.endTime.key], column.key),
      };
    });

    const columnIdToAutoHeight: Record<string, boolean> = {};

    const getNonSignalColumnKey = (column: AdornedColumn) =>
      _.includes(CONDITION_EXTRA_COLUMNS, column.key) ? i18next.t(column.shortTitle!) : column.key;
    const getColumnName = (column: AdornedColumn) => {
      const potentialSignalStatName =
        column.signalId && column.item?.name ? `${column.item.name} ${i18next.t(column.shortTitle!)}` : undefined;
      const potentialMetricName = column.metricId ? column.item?.name : undefined;
      const potentialStartOrEndName = column.isStartOrEndColumn ? i18next.t(column.shortTitle!) : undefined;
      return potentialSignalStatName ?? potentialMetricName ?? potentialStartOrEndName ?? getNonSignalColumnKey(column);
    };

    const getColumnUnits = (column: AdornedColumn) => {
      const potentialSignalUnits = column.signalId ? _.find(rawHeaders, { name: column.key })?.units : undefined;
      const potentialMetricUnits =
        column.metricId && column.item
          ? _.find(rawHeaders, { name: itemColumnsMap[String(column.item.id)].value.key })?.units
          : undefined;
      return potentialSignalUnits ?? potentialMetricUnits;
    };

    const isStringColumn = (column: AdornedColumn) => {
      const isStringSignalStat = _.find(rawHeaders, { name: column.key })
        ? _.find(rawHeaders, { name: column.key }).type === STRING_UOM
        : undefined;
      const isStringMetric = column.metricId && column.item ? isStringSeriesUtil(column.item as Signal) : undefined;
      return !!(isStringSignalStat || isStringMetric);
    };

    const headers = columns.map<ConditionTableHeader>((column, index) => ({
      key: column.key,
      name: getColumnName(column),
      units: getColumnUnits(column),
      isStringColumn: isStringColumn(column),
      formatOption: column.formatOption,
    }));

    const formatValue = (
      rawValue: unknown,
      item: TableBuilderItem | undefined,
      column: AdornedColumn,
      capsuleId: string,
    ): string => {
      // replace enums with the string representation
      const match = rawValue ? rawValue.toString().match(ENUM_REGEX) : false;
      if (match) {
        return match[2];
      }

      const value = formatMetricValue(rawValue, normalizeFormatOptions(item?.formatOptions), column);
      if (
        (columnIdToAutoHeight[column.key] === undefined || columnIdToAutoHeight[capsuleId] === undefined) &&
        value.includes('\n')
      ) {
        columnIdToAutoHeight[column.key] = true;
        columnIdToAutoHeight[capsuleId] = true;
      }

      return value;
    };

    const buildConditionTableValue = (
      column: AdornedColumn,
      row: CapsuleFormulaTableRow,
      capsuleId: string,
    ): ConditionTableValue => {
      const rowRecord = row as TableBuilderRowData;
      if (column.metricId) {
        const item = column.item as TableBuilderItem;
        const itemColumns = itemColumnsMap[String(item.id)];
        const formulaItemId = String(item.id);
        const itemId = rowRecord[itemColumns.itemId.key] as string | undefined;
        const rawValue = rowRecord[itemColumns.value.key] as string | number | null | undefined;
        const value = formatValue(rawValue, item, column, capsuleId);
        const priorityColor = rowRecord[itemColumns.priorityColor.key] as string | undefined;

        return {
          itemId,
          formulaItemId,
          value,
          priorityColor,
          rawValue,
        };
      }

      const columnRawValue = rowRecord[column.key] as string | number | null | undefined;
      return {
        value:
          column.key === SeeqNames.Properties.Duration
            ? getDuration(row)
            : formatValue(columnRawValue, column.item, column, capsuleId),
        rawValue: columnRawValue,
      };
    };

    const getDuration = (row: CapsuleFormulaTableRow) => {
      // Duration is a "hidden" property on capsules that we expose, but it is more accurate to compute it
      const duration = getCapsuleDuration(row.startTime, row.endTime);
      return _.isNil(duration) ? NULL_PLACEHOLDER : formatDuration(duration);
    };

    const rowIdCount = new Map<string, number>();
    const capsules = table.map<ConditionTableCapsule>((row) => {
      let rowId = `capsule-${row.startTime}-${row.endTime}`;
      rowIdCount.set(rowId, (rowIdCount.get(rowId) ?? 0) + 1);
      if (rowIdCount.get(rowId)! > 1) {
        rowId = `${rowId}-${rowIdCount.get(rowId)}`;
      }

      const values = _.map(columns, (column) => {
        // Start and end use the values from the capsule
        if (column.isStartOrEndColumn) {
          const value = (row as any)[column.key] ?? null;

          return { value, rawValue: value };
        }

        return buildConditionTableValue(column, row, rowId);
      });

      return {
        startTime: row.startTime,
        endTime: row.endTime,
        id: rowId,
        property: customPropertyName ? (row as any)[customPropertyName] : undefined,
        values,
      };
    });

    this.state.set(['columnIdToAutoHeight', TableBuilderMode.Condition], columnIdToAutoHeight);
    this.updateOtherColumns(TableBuilderMode.Condition, capsules);

    const tableData: ConditionTableData = { headers, capsules };
    this.state.set('fetchFailedMessage', undefined);
    this.state.set(['tableData', TableBuilderMode.Condition], tableData);
  }

  /**
   *  Sets table to be transposed (or not).
   *  If the table is not transposed (the initial default), then the time ranges will be shown in a row on top of
   *  the table.
   *  If the table is transposed, then the time ranges will be shown in a column on the left side of the table.
   *
   * @param {Object} payload - Object container
   * @param {boolean} payload.transposed - true if table is transposed, false if not
   */
  setIsTransposed(payload: { isTransposed: boolean }) {
    this.state.set(['isTransposed', this.state.get('mode')], payload.isTransposed);
  }

  /**
   *  Sets the asset over which the table will be run. If set it will be passed to the backend and the table
   *  formula will then run for each child asset and the results aggregated into a single table.
   *
   * @param {Object} payload - Object container
   * @param {string|undefined} payload.assetId - The ID of the parent asset or undefined to unset
   */
  setAssetId(payload: { assetId: string | undefined }) {
    this.state.set(['assetId', this.state.get('mode')], payload.assetId);
  }

  getAssetId(): string | undefined {
    return this.state.get('assetId', this.state.get('mode'));
  }

  getIsHomogenizeUnits() {
    return this.state.get('isHomogenizeUnits', this.state.get('mode'));
  }

  setIsHomogenizeUnits(payload: { homogenizeUnits: boolean }) {
    this.state.set(['isHomogenizeUnits', this.state.get('mode')], payload.homogenizeUnits);
  }

  setIsMigrating(payload: { isMigrating: boolean }) {
    this.state.set('isMigrating', payload.isMigrating);
  }

  setUseSignalColorsInChart(payload: { useSignalColorsInChart: boolean }) {
    this.state.set('useSignalColorsInChart', payload.useSignalColorsInChart);
  }

  setAssetPaths(payload: { assetPaths: any[] }) {
    this.state.set('assetPaths', payload.assetPaths);
  }

  setIsTableStriped(payload: { isTableStriped: boolean }) {
    this.state.set(['isTableStriped', this.state.get('mode')], payload.isTableStriped);
  }

  /**
   * Sets a message to indicate an error fetching the data. Clears the existing data since it is assumed to be
   * invalid.
   *
   * @param fetchFailedMessage - The error message
   * @param mode - The mode in which it failed
   * @param apiMessage - The error message from the API
   */
  setFetchFailedMessage({
    fetchFailedMessage,
    mode,
    apiMessage,
  }: {
    fetchFailedMessage: string;
    mode: TableBuilderMode;
    apiMessage: string;
  }) {
    this.state.set('fetchFailedMessage', {
      message: fetchFailedMessage,
      apiMessage: isBackendRowsLimitError(apiMessage) ? i18next.t('TREND_ROWS_LIMIT_ERROR') : apiMessage,
    });
    if (mode === TableBuilderMode.Simple) {
      this.state.set(['tableData', TableBuilderMode.Simple], []);
    } else {
      this.state.set(['tableData', TableBuilderMode.Condition], {
        headers: [],
        capsules: [],
      });
    }
  }

  /**
   * Removes items.
   *
   * @param {Object} payload - Object container for arguments
   * @param {Object[]} payload.items - An array of items to remove
   */
  removeItems(payload: {
    items: {
      id: string;
    }[];
  }) {
    const removedIds = _.map(payload.items, 'id');
    _.forEach(removedIds, (id) => {
      this.removeSort(id);
    });

    const removeRelatedData = () => {
      if (_.isEmpty(_.reject(this.getTableItemsProcess(), ({ id }) => _.includes(removedIds, id)))) {
        this.state.unset(['assetId', this.state.get('mode')]);
      }

      _.chain(this.getColumns())
        .filter(({ signalId, metricId }) => _.includes(removedIds, signalId) || _.includes(removedIds, metricId))
        .forEach(({ key }) => this.removeColumn({ key }))
        .value();
    };

    removeRelatedData();
    const originalMode = this.state.get('mode');
    const otherMode = originalMode === TableBuilderMode.Simple ? TableBuilderMode.Condition : TableBuilderMode.Simple;
    try {
      this.state.set('mode', otherMode);
      removeRelatedData();
    } finally {
      this.state.set('mode', originalMode);
    }
  }

  /**
   * Swaps out the specified items from one asset for the variants based off another asset.
   *
   * @param {Object} payload - Object container for arguments
   * @param {Object} payload.swaps - The items that were swapped where the keys are the swapped out ids and the
   *   values are the corresponding swapped in ids.
   * @param {Object} payload.outAsset - Asset that was swapped out
   * @param {String} payload.outAsset.id - The ID of the asset to swapped out
   * @param {String} payload.inAsset.name - The name of the asset that was swapped out
   * @param {Object} payload.inAsset - Asset that was swapped in
   * @param {String} payload.inAsset.id - The ID of the asset that was swapped in
   * @param {String} payload.inAsset.name - The name of the asset that was swapped in
   */
  swapItems(payload: { swaps: Record<string, string> }) {
    _.forEach(payload.swaps, (swappedInId, swappedOutId) => {
      _.forEach([TableBuilderMode.Condition, TableBuilderMode.Simple], (mode) => {
        _.forEach(this.state.get('columns', mode), (column, index) => {
          if (column.signalId === swappedOutId) {
            this.state.merge(['columns', mode, index], {
              signalId: swappedInId,
              key: this.getColumnKeyFromSignalId(column, swappedInId),
            });
          }
          if (column.metricId === swappedOutId) {
            this.state.merge(['columns', mode, index], {
              metricId: swappedInId,
              key: swappedInId,
            });
          }
        });
      });
    });
  }

  /**
   * Enables the Metric Value column if a new simple metric was created and its statistic is not already displayed
   * in the table. It only happens if the table view is active and table mode is simple.
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.id - Simple metric identifier
   * @param {ThresholdMetricInputV1} payload.definition - Simple metric definition
   */
  handleSimpleMetricCreated(payload: {
    id: string;
    definition: {
      aggregationFunction: string;
    };
  }) {
    const isMetricValueEnabled = _.some(this.columns, ['key', COLUMNS_AND_STATS.metricValue.key]);
    const isAggregationFunctionAlreadyDisplayed = _.some(this.columns, [
      'stat',
      payload.definition.aggregationFunction,
    ]);

    if (
      sqWorksheetStore.view.key !== WORKSHEET_VIEW.TABLE ||
      !this.getIsSimpleMode() ||
      isMetricValueEnabled ||
      isAggregationFunctionAlreadyDisplayed
    ) {
      return;
    }

    this.addColumn({ column: { key: COLUMNS_AND_STATS.metricValue.key } });
    infoToast({
      messageKey: 'TABLE_BUILDER.METRIC_VALUE_COLUMN_AUTOMATICALLY_ENABLED',
    });
  }

  /**
   * Removes metric information from the specified column. If an item is removed, all metric info entries are
   * removed. If a metric is removed, the corresponding metric info is removed.
   * @param removedIds - the item ids to look for
   * @param columnIndex - the column index
   */
  removeColumnMetricInfo(removedIds: string[], columnIndex: number) {
    const cursor = this.state.select('columns', this.state.get('mode'), columnIndex, 'metrics');
    _.forEach(cursor.get(), (metricInfo, itemId) => {
      if (_.includes(removedIds, metricInfo.id) || _.includes(removedIds, itemId)) {
        cursor.unset(itemId);
      }
    });
  }

  /**
   * Sets the chart view to be enabled or not
   * @param payload - payload.enabled boolean if chart view is enabled
   */
  setChartView(payload: { enabled: boolean }) {
    this.state.set(['chartView', 'enabled'], payload.enabled);
  }

  /**
   * Sets the tree map view to be enabled or not
   * @param payload - payload.enabled boolean if tree map view is enabled
   */
  setTreemapView(payload: { enabled: boolean }) {
    this.state.set(['treemapView', 'enabled'], payload.enabled);
  }

  /**
   * Sets the chart view to be enabled or not
   * @param payload - payload.enabled boolean if chart view is enabled
   */
  setConditionChartView(payload: { conditionEnabled: boolean }) {
    this.state.set(['chartView', 'conditionEnabled'], payload.conditionEnabled);
  }

  /**
   * Sets flag to expande or collapse all row groups
   * @param payload - payload.areAllRowsExpanded true means expand false means collapse undefined means noop*/
  setAreAllRowsExpanded(payload: { areAllRowsExpanded: boolean | undefined }) {
    this.state.set('areAllRowsExpanded', payload.areAllRowsExpanded);
  }

  /**
   * Settings for the chart view
   * @param payload - payload.settings object with settings for the chart view
   */
  setChartViewSettings(payload: { settings: ChartModel | undefined }) {
    this.state.select(['chartView', 'settings']).set(payload.settings);
  }

  /**
   * Settings for the condition table chart view
   * @param payload - payload.conditionSettings object with settings for the ag grid chart configuration
   */
  setChartViewConditionSettings(payload: { conditionSettings?: ChartModel }) {
    this.state.select(['chartView', 'conditionSettings']).set(payload.conditionSettings);
  }

  setChartMenuSettings(payload: AgGridChartMenuSettings) {
    this.state.set(['chartMenuSettings', this.state.get('mode')], payload);
  }

  /**
   * Settings for the treemap view settings
   * @param payload - payload.settings object with settings for the treemap view
   */
  setTreemapSettings(payload: { settings?: TreemapViewSettings }) {
    this.state.set(['treemapView', 'settings'], payload.settings);
  }

  /**
   *  Set the distinctStringValueMap from data for the Simple Table
   *
   * @param payload object container for arguments
   * @param payload.stringValueTables: array of the tables, each containing distinct string values for a column
   * @param payload.columnKeysNamesList: list of objects containing columnKeys (for the displayed table) and
   *  columnNames (for the tables we got back from the calc engine) - one columnKey may correspond to more than
   *  one columnName
   */
  setSimpleDistinctStringValues(payload: {
    stringValueTables: {
      data: any[];
      headers: { name: string }[];
    }[];
    columnKeysNamesList: {
      columnKey: string;
      columnNames: string[];
    }[];
  }) {
    if (_.isEmpty(payload.stringValueTables) || !this.getIsSimpleMode()) {
      return;
    }
    const distinctStringValueMap = _.cloneDeep(this.state.get('distinctStringValueMap', TableBuilderMode.Simple));
    for (let i = 0; i < payload.stringValueTables.length; i++) {
      const tableData = payload.stringValueTables[i].data;
      const tableHeaders = payload.stringValueTables[i].headers;
      const { columnKey, columnNames } = payload.columnKeysNamesList[i];
      let distinctValues: string[] = [];
      _.forEach(columnNames, (columnName) => {
        const index = _.findIndex(tableHeaders, { name: columnName });
        distinctValues = distinctValues.concat(_.chain(tableData).map(`[${index}]`).reject(_.isNil).value());
      });
      distinctStringValueMap[columnKey] = distinctValues;
    }
    _.forEach(_.keys(distinctStringValueMap), (existingKey) => {
      if (!_.includes(_.map(payload.columnKeysNamesList, 'columnKey'), existingKey)) {
        delete distinctStringValueMap[existingKey];
      }
    });
    this.state.set(['distinctStringValueMap', TableBuilderMode.Simple], distinctStringValueMap);
  }

  /**
   *  Set the distinctStringValueMap from data for the Condition Table
   *
   * @param payload object container for arguments
   * @param payload.stringValueTables: array of the tables, each containing distinct string values for a column
   * @param payload.columnKeysNamesList: list of objects containing columnKeys (for the displayed table) and
   *  columnNames (for the tables we got back from the calc engine)
   */
  setConditionDistinctStringValues(payload: {
    stringValueTable: { data: { table: TableItem[] } };
    columnKeyAndName: { columnKey: string; columnName: string };
  }) {
    const tableData = payload.stringValueTable.data.table;
    const columnKey = payload.columnKeyAndName.columnKey;
    const columnName = payload.columnKeyAndName.columnName;
    this.state.set(['distinctStringValueMap', TableBuilderMode.Condition, columnKey], _.map(tableData, columnName));
  }

  enableRowGroup(payload: { key: string }) {
    if (this.state.get(['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'grouping'])) {
      return;
    }
    this.state.set(['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'grouping'], true);
    const currentHighestRowGroupOrder: number | undefined = _.max(
      this.state
        .get(['columns', this.state.get('mode')])
        .map((column: ColumnOrRow) => column.rowGroupOrder)
        .filter((order: number | undefined) => _.isNumber(order)),
    );
    const newRowGroupIndex = (currentHighestRowGroupOrder ?? -1) + 1;
    this.state.set(
      ['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'rowGroupOrder'],
      newRowGroupIndex,
    );
  }

  disableRowGroup(payload: { key: string }) {
    const index = this.getColumnIndex(payload.key, true);
    if (index > -1) {
      this.state.unset(['columns', this.state.get('mode'), index, 'grouping']);
      // We don't need to adjust the ordering of other columns because ag-grid uses relative ordering. So if the only
      // columns left have orders of 1, 3, 5, they will be grouped in that order.
      this.state.unset(['columns', this.state.get('mode'), index, 'rowGroupOrder']);
    }
  }

  setRowGroupShow(payload: { fullPath: string; expanded: boolean }) {
    const index = _.findIndex(
      this.state.get(['rowGroupPaths', this.state.get('mode')]),
      (path) => path === payload.fullPath,
    );
    if (payload.expanded && index < 0) {
      this.state.push(['rowGroupPaths', this.state.get('mode')], payload.fullPath);
    } else if (!payload.expanded && index > -1) {
      this.state.splice(['rowGroupPaths', this.state.get('mode')], [index, 1]);
    }
  }

  toggleRowGrouping() {
    if (this.autoGroupColumn) {
      this.state.unset(['autoGroupColumn', this.state.get('mode')]);
    } else {
      this.state.set(['autoGroupColumn', this.state.get('mode')], {});
    }
  }

  setRowGroupOrder(payload: { order: string[] }) {
    payload.order.forEach((key, index) => {
      this.state.set(['columns', this.state.get('mode'), this.getColumnIndex(key), 'rowGroupOrder'], index);
    });
  }

  setAggregationFunction(payload: { key: string; aggregationFunction: AgGridAggregationFunction }) {
    if (payload.aggregationFunction === 'none') {
      this.state.unset(['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'aggregationFunction']);
    } else {
      this.state.set(
        ['columns', this.state.get('mode'), this.getColumnIndex(payload.key), 'aggregationFunction'],
        payload.aggregationFunction,
      );
    }
  }

  findNonConflictingSimpleMetrics(itemIds: string[]): MetricsMap {
    return (
      _.chain(
        getAllItems({
          workingSelection: false,
          itemTypes: [ITEM_TYPES.METRIC],
        }),
      )
        .reject((metric) => isItemRedacted(metric))
        .filter((metric) => metric.definition?.processType === ProcessTypeEnum.Simple)
        .filter((metric) => _.includes(itemIds, metric.definition.measuredItem.id))
        // filter out the conflictual metrics (multiple metrics with the same aggregation function for the same item)
        .thru((metrics) =>
          _.reject(metrics, (metric) =>
            _.some(
              metrics,
              (otherMetric) =>
                metric.id !== otherMetric.id &&
                metric.definition.measuredItem.id === otherMetric.definition.measuredItem.id &&
                metric.definition.aggregationFunction === otherMetric.definition.aggregationFunction,
            ),
          ),
        )
        .transform((metricsMap, metric) => {
          const measuredItemId = metric.definition.measuredItem.id;
          const measuredItemType = findItemIn(
            // @ts-ignore `findItemIn` expects a store that is in `TREND_STORES` but sqTrendStore is not
            [sqTrendSeriesStore, sqTrendConditionStore, sqTrendStore],
            measuredItemId,
          )?.itemType;
          const aggregationFunction =
            metric.definition.aggregationFunction ??
            // Fallback to last value for signals, undefined for conditions
            (measuredItemType === ITEM_TYPES.CONDITION
              ? undefined
              : _.find(TREND_SIGNAL_STATS, SIMPLE_METRIC_FALLBACK_STATISTIC)?.stat);
          metricsMap[measuredItemId] = metricsMap[measuredItemId] ?? {};
          metricsMap[measuredItemId][aggregationFunction] = metric.id;
        }, {} as MetricsMap)
        .value()
    );
  }

  setItemFinderId(payload: { itemFinderId?: string }) {
    this.state.set('itemFinderId', payload.itemFinderId);
  }

  setItemFinderInput(payload: { itemFinderInput?: ItemFinderInputV1 }) {
    this.state.set('itemFinderInput', payload.itemFinderInput);
  }

  setTableDefinitionId(payload: { tableDefinitionId?: string }) {
    this.state.set('tableDefinitionId', payload.tableDefinitionId);
  }

  setIsFormulaAcrossTable(payload: { isFormulaAcrossTable: boolean }) {
    this.state.set(['isFormulaAcrossTable', this.state.get('mode')], payload.isFormulaAcrossTable);
  }

  setIsFormulaAcrossTableProcessing(payload: { isFormulaAcrossTableProcessing: boolean }) {
    this.state.set('isFormulaAcrossTableProcessing', payload.isFormulaAcrossTableProcessing);
  }

  /**
   * Builds the simple table data for presentation from the scaling table to simple table flow, where the simple
   * table results come from the graphQL getTable API and not formula/run. The structure and location of various
   * data elements is different from the formula/run flow. Number formatting is resolved from the caller-supplied
   * items array rather than from per-row backend properties.
   *
   * @param table - the simple table data and headers from the graphQL getTable API
   * @param trendItems - trend store items for the current table, used to resolve per-item number format options
   */
  setFormulaAcrossSimpleTableData({
    table,
    trendItems = [],
  }: {
    table: SimpleTableResultTable;
    trendItems?: { id: string; formatOptions?: FormatOptions | string }[];
  }) {
    const columns = this.columns;
    const columnIdToAutoHeight: Record<string, boolean> = {};
    const columnIndexes = columns.map((column) => {
      return table.headers.findIndex((header) => [column.backingTableColumn, column.stat].includes(header.name));
    });

    const getCellValue = (index: number, resultRow: any[], column: ColumnOrRowWithDefinitions, itemId: string) => {
      const item = trendItems.find((it) => it.id === itemId);
      const formatOptions = normalizeFormatOptions(item?.formatOptions);
      const formattedValue = formatMetricValue(resultRow[index], formatOptions, column);
      if (
        (columnIdToAutoHeight[column.key] === undefined || columnIdToAutoHeight[itemId] === undefined) &&
        formattedValue.includes('\n')
      ) {
        columnIdToAutoHeight[column.key] = true;
        columnIdToAutoHeight[itemId] = true;
      }
      const rawValue = resultRow[index];

      let priorityColor: string | undefined;

      const measuredItemIdIndex = table.headers.findIndex((header) => header.name === 'metricValue().measuredItemId');

      if (measuredItemIdIndex !== -1 && _.isNil(resultRow[measuredItemIdIndex])) {
        const aggregationFunctionIndex = table.headers.findIndex(
          (header) => header.name === 'item id.properties.AggregationFunction',
        );
        if (aggregationFunctionIndex !== -1) {
          const metricMeasuringThisItem = _.find(
            table.data,
            (dataRow) =>
              dataRow[measuredItemIdIndex] === itemId &&
              column.stat &&
              dataRow[aggregationFunctionIndex] === column.stat,
          );

          if (!_.isNil(metricMeasuringThisItem)) {
            const metricColorIndex = table.headers.findIndex((header) => header.name === 'metricValue().color');
            if (metricColorIndex !== -1) {
              priorityColor = metricMeasuringThisItem[metricColorIndex];
            }
          }
        }
      }

      const unitIndex = table.headers.findIndex(
        (header) => header.name === COLUMNS_AND_STATS.valueUnitOfMeasure.backingTableColumn,
      );

      function determineUnits(unitFromData: any, column: ColumnOrRowWithDefinitions): any {
        if (
          (!!column.propertyName && column.key !== COLUMNS_AND_STATS.valueUnitOfMeasure.key) ||
          unitFromData === STRING_UOM ||
          isPropertyColumnType(column) ||
          !!column.assetPathId
        ) {
          return undefined;
        } else if (
          [
            COLUMNS_AND_STATS['statistics.percentDuration'].key,
            COLUMNS_AND_STATS['statistics.percentGood'].key,
            COLUMNS_AND_STATS['statistics.count'].key,
            COLUMNS_AND_STATS['statistics.totalDuration'].key,
          ].includes(column.key)
        ) {
          return (COLUMNS_AND_STATS as any)[column.key].backingTableUom;
        } else {
          return unitFromData;
        }
      }

      const unitFromData = unitIndex !== -1 ? resultRow[unitIndex] : undefined;
      const thresholdMetricUomIndex = table.headers.findIndex((header) => header.name === 'metricValue().uom');
      const unitFromThresholdMetric = thresholdMetricUomIndex !== -1 ? resultRow[thresholdMetricUomIndex] : undefined;
      const units = determineUnits(unitFromData || unitFromThresholdMetric, column);

      return {
        value: formattedValue,
        units,
        rawValue,
        ...(priorityColor && { priorityColor }),
      };
    };

    const sourceColumnIndex = table.headers.findIndex(
      (header) => header.name === SeeqNames.MaterializedTables.FormulaAcross.SourceColumnIdColumn,
    );

    const formattedTableRows: SimpleTableRow[] = _.chain(table.data)
      .flatMap((resultRow): SimpleTableRow => {
        return {
          itemId: resultRow[0],
          formulaItemId: resultRow[sourceColumnIndex] ?? resultRow[0],
          cells: columns.map((column, index) => {
            const columnIndex = columnIndexes[index];

            if (column.key === COLUMNS_AND_STATS.valueUnitOfMeasure.key) {
              const uom = resultRow[columnIndex];
              if (uom === STRING_UOM) {
                return { value: '', rawValue: uom };
              } else if (_.isNil(uom)) {
                const metricUomIndex = table.headers.findIndex((header) => header.name === 'metricValue().uom');
                if (metricUomIndex > -1) {
                  return { value: resultRow[metricUomIndex], rawValue: resultRow[metricUomIndex] };
                }
              }
              return { value: uom, rawValue: uom };
            } else if (_.isNil(resultRow[columnIndex])) {
              return {
                value: formatMetricValue(null, {} as FormatOptions),
                rawValue: null,
              };
            } else if (column.key === COLUMNS_AND_STATS.metricValue.key) {
              return {
                ...getCellValue(columnIndex, resultRow, column, resultRow[0]),
                priorityColor: resultRow[columnIndex + 1],
                metricId: resultRow[0],
              };
            } else if (column.key === COLUMNS_AND_STATS['statistics.totalDuration'].key) {
              return {
                value: formatDuration(secondsToMillis(resultRow[columnIndex])),
                rawValue: resultRow[columnIndex],
              };
            } else {
              return getCellValue(columnIndex, resultRow, column, resultRow[0]);
            }
          }),
        };
      })
      .value();

    const tableDataByIteratingOnDataOnly = _.chain(formattedTableRows)
      .compact()
      .thru((rows: SimpleTableRow[]) => this.maybeApplyDefaultSort(rows, [], columns))
      .value();

    this.state.set(['columnIdToAutoHeight', TableBuilderMode.Simple], columnIdToAutoHeight);
    this.updateOtherColumns(TableBuilderMode.Simple, tableDataByIteratingOnDataOnly);

    this.state.set('fetchFailedMessage', undefined);
    this.state.set(['tableData', TableBuilderMode.Simple], tableDataByIteratingOnDataOnly);
  }

  /**
   * Refreshes the table formatting with updated trend items without fetching new data from the backend.
   * This is used when cosmetic properties (NumberFormat, StringFormat, DisplayUnit) change.
   * Re-formats existing tableData using stored rawValues and updated formatOptions from trend stores.
   */
  refreshFormulaAcrossTableFormatting() {
    // Wait for trend stores to be updated with new formatOptions
    this.waitFor(['sqTrendStore', 'sqTrendSeriesStore', 'sqTrendMetricStore', 'sqTrendConditionStore'], () => {
      const tableData = this.state.get(['tableData', TableBuilderMode.Simple]) as SimpleTableRow[] | undefined;
      if (!tableData || tableData.length === 0) {
        return;
      }

      // Get updated trend items with current formatOptions from trend stores
      const trendItemsMap = new Map(
        this.getTableItems()
          .map((tableItem) => findItemIn(getTrendStores(), tableItem.id))
          .filter((item): item is NonNullable<typeof item> => item !== undefined)
          .map((item) => [item.id, item]),
      );

      const columns = this.columns;

      // Re-format each row using its stored rawValues and updated formatOptions
      const reformattedData = tableData.map((row) => {
        const trendItem = trendItemsMap.get(row.itemId);
        const formatOptions = normalizeFormatOptions(trendItem?.formatOptions);

        return {
          ...row,
          cells: row.cells.map((cell, colIndex) => {
            const column = columns[colIndex];
            if (!column || cell.rawValue === undefined) {
              return cell;
            }

            const formattedValue = formatMetricValue(cell.rawValue, formatOptions, column);
            return {
              ...cell,
              format:
                formattedValue !== cell.rawValue && formatOptions.format ? { format: formatOptions.format } : undefined,
              value: formattedValue,
            };
          }),
        };
      });

      this.state.set(['tableData', TableBuilderMode.Simple], reformattedData);
    });
  }

  /**
   * Refreshes condition table formatting with updated trend items without fetching new data from the backend.
   * This is used when cosmetic properties (NumberFormat, StringFormat, DisplayUnit) change.
   * Re-formats existing tableData using stored rawValues and updated formatOptions from trend stores.
   */
  refreshConditionTableFormatting() {
    // Wait for trend stores to be updated with new formatOptions
    this.waitFor(['sqTrendStore', 'sqTrendSeriesStore', 'sqTrendMetricStore'], () => {
      const tableData = this.state.get(['tableData', TableBuilderMode.Condition]) as ConditionTableData | undefined;
      if (!tableData || !tableData.capsules || tableData.capsules.length === 0) {
        return;
      }

      const columns = this.conditionTableColumns.rows;
      const columnsByKey = new Map(columns.map((column) => [column.key, column]));
      const getColumnForHeader = (header?: ConditionTableHeader) => (header ? columnsByKey.get(header.key) : undefined);
      const getFormatOptionsForColumn = (column?: ColumnOrRowWithDefinitions): FormatOptions => {
        const itemId = column?.signalId ?? column?.metricId;
        if (!column || !itemId) {
          return {};
        }

        const itemFinder = column.signalId ? sqTrendSeriesStore.findItem : sqTrendMetricStore.findItem;
        const item = itemFinder?.(itemId);
        return normalizeFormatOptions(item?.formatOptions);
      };

      const reformattedHeaders = tableData.headers.map((header, colIndex) => {
        const column = getColumnForHeader(header);
        if (!column) {
          return header;
        }

        const formatOptions = getFormatOptionsForColumn(column);

        return {
          ...header,
          formatOption: formatOptions.format ? { format: formatOptions.format } : undefined,
        };
      });

      // Re-format each capsule's values using stored rawValues and updated formatOptions from trend stores
      const reformattedCapsules = tableData.capsules.map((capsule) => {
        const reformattedValues = capsule.values.map((cellValue, colIndex) => {
          const column = getColumnForHeader(tableData.headers[colIndex]);
          if (!column || cellValue.rawValue === undefined) {
            return cellValue;
          }

          const formatOptions = getFormatOptionsForColumn(column);
          const formattedValue = formatMetricValue(cellValue.rawValue, formatOptions, column);
          return {
            ...cellValue,
            value: formattedValue,
          };
        });

        return {
          ...capsule,
          values: reformattedValues,
        };
      });

      this.state.set(['tableData', TableBuilderMode.Condition], {
        ...tableData,
        headers: reformattedHeaders,
        capsules: reformattedCapsules,
      });
    });
  }

  /**
   * Refreshes simple table formatting with updated trend items without fetching new data from the backend.
   * This is used when cosmetic properties (NumberFormat, StringFormat, DisplayUnit) change.
   * Re-formats existing tableData using stored rawValues and updated formatOptions from trend stores.
   */
  refreshSimpleTableFormatting() {
    // Wait for trend stores to be updated with new formatOptions
    this.waitFor(['sqTrendStore', 'sqTrendSeriesStore', 'sqTrendMetricStore', 'sqTrendConditionStore'], () => {
      const tableData = this.state.get(['tableData', TableBuilderMode.Simple]) as SimpleTableRow[] | undefined;
      if (!tableData || tableData.length === 0) {
        return;
      }

      // Get updated trend items with current formatOptions from trend stores
      const trendItemsMap = new Map(
        this.getTableItems()
          .map((tableItem) => findItemIn(getTrendStores(), tableItem.id))
          .filter((item): item is NonNullable<typeof item> => item !== undefined)
          .map((item) => [item.id, item]),
      );

      const columns = this.columns;

      // Re-format each row using its stored rawValues and updated formatOptions
      const reformattedData = tableData.map((row) => {
        const trendItem = trendItemsMap.get(row.formulaItemId);
        const formatOptions = normalizeFormatOptions(trendItem?.formatOptions);

        return {
          ...row,
          cells: row.cells.map((cell, colIndex) => {
            const column = columns[colIndex];
            if (!column || cell.rawValue === undefined) {
              return cell;
            }

            const formattedValue = formatMetricValue(cell.rawValue, formatOptions, column);
            return {
              ...cell,
              format:
                formattedValue !== cell.rawValue && formatOptions.format ? { format: formatOptions.format } : undefined,
              value: formattedValue,
            };
          }),
        };
      });

      this.state.set(['tableData', TableBuilderMode.Simple], reformattedData);
    });
  }

  /**
   * Sets the asset path state hash used to detect changes in asset path configuration
   * @param payload - payload.hash string representing the current asset path state
   */
  setAssetPathStateHash(payload: { hash: string }) {
    this.state.set('assetPathStateHash', payload.hash);
  }
}
